import { Component, OnInit, OnDestroy, inject, Input, Output, EventEmitter, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { FilingInfoConfirmationDialogComponent } from '../../common/filing-info-confirmation-dialog/filing-info-confirmation-dialog.component';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Subject,
  takeUntil,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { FilingModeService } from '../../../services/mbrs/filing-mode.service';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { WarningResult, WarningRule } from '../../../models/validation-warning.model';
import { FormSchema, FormControlSchema } from '../../../models/form-schema.model';
import { applyInputFilter, onPasteFilter as applyPasteFilter, applyTrimNormalizersToForm } from '../../../utils/input-filter';
import { mapSchemaValidators } from '../../../utils/schema-validators.util';
import { isHeldDisabledByAnotherRule } from '../../../utils/control-lock-state.util';
import {
  ConversionAuditTrail,
  DeleteConversionTarget,
  MbrsStatusEnum,
} from '../../../../types/commons';
import { DeleteConversionComponent } from '../../common/delete-conversion/delete-conversion.component';
import { FilingActionButtonsComponent } from '../../common/filing-action-buttons/filing-action-buttons.component';
import { ConversionEnginePickerComponent } from '../../common/conversion-engine-picker/conversion-engine-picker.component';
import { handleDateInputChange, handleDatePaste } from '../../../utils/helpers';
import { FORM_AUTOSAVE_DEBOUNCE_MS } from '../../../utils/debounce-times.util';
import {
  checkCurrentEndFloor,
  checkCurrentRange,
  checkDateSequence,
  checkPreviousRange,
  FILING_INFO_CONDITIONAL_RULES,
  FILING_INFO_FIELD_KEYS,
  isBeforeSupportedFinancialYearEnd,
  isUnconditionallyRequired,
  MIN_SUPPORTED_CURRENT_FINANCIAL_YEAR_END,
  UNSUPPORTED_CURRENT_FINANCIAL_YEAR_END_MESSAGE,
} from '../../../services/mbrs/validators/filing-info-common.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe, FixedPlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { SelectClearIconBehaviorDirective } from '../../../directives/select-clear-icon-behavior.directive';
import {
  FILING_INFO_DRIVER_CLEAR_EDGES,
  isClearedValue,
  resolveDriverClearTargets,
} from '../../../services/mbrs/validators/filing-info-driver-clear.rules';
import { finalizeSheetInit } from '../../../utils/sheet-init-finalize.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { coerceSchemaDateStrings } from '../../../utils/date-string-coercion.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchJsonAsset,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
// FormSchema and FormControlSchema interfaces now imported from shared model

@Component({
  selector: 'app-role0-000000-filing-prerequisites',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Select,
    SelectClearIconBehaviorDirective,
    DatePicker,
    TooltipModule,
    Toast,
    ConfirmDialog,
    DeleteConversionComponent,
    FilingActionButtonsComponent,
    ConversionEnginePickerComponent,
    FilingInfoConfirmationDialogComponent,
    PlaceholderPipe,
    FixedPlaceholderPipe
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './role0-000000-filing-prerequisites.component.html',
  styleUrls: ['./role0-000000-filing-prerequisites.component.css'],
})
export class Role0000000FilingPrerequisitesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private confirmationService = inject(ConfirmationService);
  private filingModeService = inject(FilingModeService);
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);
  private router = inject(Router);

  // Expose isEditedMode signal to template for conditional asterisk display
  isEditedMode = this.filingModeService.isEditedMode;

  // Store validation warnings per field (qname -> warnings array)
  fieldWarnings: { [qname: string]: WarningResult[] } = {};

  // Inputs from parent filing-form-container for delete-conversion component
  @Input() conversionId: string | null = null;
  @Input() companyName: string = '';
  @Input() auditYear: number = 0;
  @Input() conversionStatus: number | null = null;
  /** Created/modified trail for the delete dialog; `null` until the container's `xbrl-data-listing` read answers. */
  @Input() conversionAudit: ConversionAuditTrail | null = null;
  @Input() isOnlyFilingSheet: boolean = false;

  /**
   * The engine that last processed this conversion, passed down by
   * `FilingFormContainerComponent`. Null when the backend has not recorded one.
   */
  @Input() lastUsedEngine: string | null = null;

  /**
   * Engine chosen for the Phase 2 run this sheet's Submit kicks off. Read by
   * the container in `onRole0Submit` and appended to the payload; null means
   * "omit the field", and the server applies its configured default.
   *
   * Component-local by decision: it resets when the container's `*ngIf`
   * remounts this sheet. See ConversionEnginePickerComponent's docblock.
   */
  selectedEngine: string | null = null;

  onEngineChange(engine: string | null): void {
    this.selectedEngine = engine;
  }

  /**
   * Phase 1 is the only phase that mounts this sheet, and a Phase 1 conversion
   * always needs a fresh Phase 2 run, so the picker is never disabled here.
   * Contrast role 1, which gates on `hasUnsubmittedChanges()`.
   *
   * A named method rather than an inline template expression so the
   * role0/role1 parity spec can reach it — those specs use a prototype host,
   * not TestBed. Pinned in `filing-info-submit-gate-parity.spec.ts`.
   */
  isEnginePickerDisabled(): boolean {
    return false;
  }

  // Dialog states
  /**
   * "Are there unsubmitted changes on THIS sheet?" — the changes-term shared by
   * the Submit gate and Cancel, and the same modified-vs-original verdict that
   * drives the sheet selector's pencil (`isSheetModified`).
   *
   * Deliberately NOT `filingForm.pristine`, which only answers "has the user
   * touched this form since it mounted?" and never recovers once dirtied. So
   * after the user edits a field and types the ORIGINAL value back, a
   * pristine-gated Cancel still raised "Any unsaved changes will be lost" over
   * a form with nothing to lose, and a pristine-gated Submit stayed enabled
   * with nothing to submit. Kept identical to role1's field of the same name —
   * see that one for the full rationale and the remount case that applies
   * there but not here (Phase 1 is a single-sheet, single-mount surface).
   *
   * Refreshed from state rather than read straight from the template: the
   * comparison is a deep compare over every field.
   */
  hasUnsubmittedChanges = signal<boolean>(false);

  isArchiveDialogOpen = signal<boolean>(false);
  isEditWarningDialogOpen = signal<boolean>(false);

  // Event emitter for submit action
  @Output() submitForm = new EventEmitter<any>();

  filingForm!: FormGroup;
  schema!: FormSchema;
  typeDict: any = {};
  isLoading = true;

  // Track if Role-0 was initially completed (to show edit warning)
  private wasInitiallyCompleted = false;
  private hasShownEditWarning = false;
  /** True while the container's phase-two submit is in flight. Drives the
   * Submit-button spinner and keeps the commit deferred until the backend
   * confirms success (see onSubmitSucceeded / onSubmitFailed). */
  isSubmitting = false;

  // Dropdown options
  dropdownOptions: { [key: string]: any[] } = {};

  // Filtered dropdown options (for conditional filtering)
  filteredDropdownOptions: { [key: string]: any[] } = {};

  // Track which fields should be hidden
  hiddenFields: { [key: string]: boolean } = {};

  /**
   * Apply-callbacks registered by the reactive-rule engines below. Re-invoked
   * after an API hydration patch, which uses `emitEvent: false` and therefore
   * never wakes those engines' own `valueChanges` subscribers.
   *
   * Generalises the existing `reapplyConditionalValidation()` — same problem,
   * same shape, six rule families wider. Registration ORDER is the dependency
   * order (see reapplyReactiveRules).
   */
  private reactiveRuleAppliers: Array<() => void> = [];

  /**
   * Re-entrancy guard for `setupDriverClearCascade`. The cascade clears its
   * targets with `emitEvent: true` (so the TypeOfSubmission auto-set seam
   * fires), and a cleared target is itself a driver — which re-enters the same
   * subscriber. The closure is already complete on the first pass, so the
   * re-entrant passes are collapsed rather than repeated.
   */
  private isDriverClearCascading = false;

  /**
   * Re-entrancy guard for the `setupLockWhenRules` re-assert subscriber
   * (Product ruling 2026-08-10). That subscriber writes `setValueTo` back
   * onto the target the instant it observes an empty value while the
   * trigger still matches the locking condition — and since `setValueTo` is
   * itself written with `emitEvent: true`, the write re-enters the same
   * `targetControl.valueChanges` subscriber. `setValueTo` is always a
   * non-empty literal, so the emptiness check alone would already stop the
   * second pass — this flag is a belt-and-braces guard, not the only line
   * of defence.
   */
  private isLockWhenReasserting = false;

  // Flag to track if we're loading saved state (to prevent auto-save during load)
  private isLoadingState = false;

  // Store custom date validator functions for reuse
  private dateValidators: { [key: string]: any } = {};

  // Track if this is first conversion (only filingInfo & filingPrerequisites have data)
  // When true, show fields with hideAfterFirstConversion flag
  // When false, hide fields with hideAfterFirstConversion flag
  // Also used to determine if Submit button should be enabled without changes
  isFirstConversion = true;

  /**
   * Registration order note (2026-08-10): role0 wires its reactive-rule
   * engines disable -> condOptions -> bankNegara -> lock -> autoset ->
   * methods, which is a DIFFERENT relative order from role1's disable ->
   * lock -> condOptions -> bankNegara -> autoset -> methods. This is
   * harmless today — the only load-bearing constraint across both sheets is
   * lock-before-autoset, which holds in both orderings, and Basis carries no
   * `optionsFilteredBy` for condOptions to race against. Keep this in mind
   * before adding a new engine on either sheet: verify it against the
   * ordering below, not against the other sheet's.
   */
  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '0',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    try {
      // Step 1 is marked only AFTER the wait, like every other sheet. It used to
      // be marked on arrival on the claim that this sheet never waits, but
      // `finalizeSheetInit` awaits the same flag, so a hydration hang reported
      // `steps reached: [1,…]` instead of the designed `[]`. Awaiting first puts
      // a stall at the first step; the schema fetch now starts after it (static,
      // cached asset, so the cost is small).
      await this.stateService.waitForMbrsHydration('role0');
      this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);
      await this.loadSchema();
      await this.loadTypeDict();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);

      // Check if Role-0 was initially completed (for edit warning)
      this.wasInitiallyCompleted = this.isRole0Completed();

      // Check if this is first conversion (determines hideAfterFirstConversion field visibility)
      this.checkIfFirstConversion();

      this.setupStatePersistence();
      this.setupUnsubmittedChangesTracking();
      this.setupStateListener();
      this.setupWarningEvaluation();
      this.setupConditionalValidation();
      this.setupFinancialYearDateValidation();
      this.setupFieldDisabling();
      this.setupConditionalOptions();
      this.setupBankNegaraFieldManagement();

      // Developer Note [Not supported in role0]: KFI-MFRS / KFI-MPERS are out
      // of scope for role0 across both conversion phases. Re-enable this call
      // (and the definition below) only if the KFI variants are ever adopted
      // for role0. See docs/sheet-specs/00_filing-prerequisites.md Field #18.
      // this.setupKFIMFRSExemptionFieldManagement();
      
      // MUST be registered BEFORE setupTypeOfSubmissionAutoSet (item C,
      // 2026-08-10): since item C, applyLockRule's SYSTEM writes (initial
      // mount, reactiveRuleAppliers hydration re-apply) are silent
      // (emitEvent:false), so they no longer cross-fire a Basis subscriber
      // registered after them. Type of submission's correct derivation now
      // depends ENTIRELY on setupTypeOfSubmissionAutoSet's own applier
      // re-reading Basis's already-locked `.value` directly, which only
      // happens if it is registered — and its reactiveRuleAppliers entry
      // pushed — AFTER this call. Swapping this order reproduces the
      // regression pinned by the "reversed order" guard in
      // `role0-hydration-reapply.spec.ts` (describe: "ngOnInit order:
      // setupLockWhenRules vs setupTypeOfSubmissionAutoSet").
      this.setupLockWhenRules();
      this.setupTypeOfSubmissionAutoSet();
      // MUST be registered AFTER setupTypeOfSubmissionAutoSet: both subscribe
      // to Basis's own valueChanges, and this one needs to be the LAST
      // subscriber on that control to avoid a stale re-entrant emission. See
      // the docblock on setupLockWhenReassert for the measured failure mode.
      this.setupLockWhenReassert();
      // Developer Note [Declarative refactor]: Field #31
      // (MethodUsedForRepresentingChangesInAnEntitysEquity) is now handled by
      // the schema via `readonly` + `defaultValue` + `disabledWhen` with
      // `hideWhenDisabled` in role-0.form.json. The imperative manager below
      // and its call are retained as commented-out reference. Re-enable only
      // if the declarative rule regresses the MPERS-show / MFRS-hide UX.
      // this.setupEquityMethodFieldManagement();
      this.setupMethodFieldsManagement();
      this.setupSetDefaultWhenRules();

      // MUST be registered LAST: subscribers fire in registration order, and
      // this one clears values that earlier subscribers (lockWhen re-enable,
      // option re-filtering, method-field management) have just re-evaluated.
      this.setupDriverClearCascade();

      // Deterministic finalization (replaces the legacy chained
      // setTimeout(100 -> 600) that used to live in loadSavedState). Blocks
      // the form reveal until the container's MBRS-data hydration completes
      // (if any) — finalizeSheetInit performs that wait internally, so it is
      // not duplicated here. Extracted to finalizeInit() so the non-init
      // callers (onCancel / onRevert) can re-finalize after their own
      // loadSavedState() calls — see finalizeInit's doc comment.
      await this.finalizeInit();

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  /**
   * Check if Role-0 is completed (has valid data)
   */
  private isRole0Completed(): boolean {
    const sheetLevelStatus = this.stateService.getSheetLevelStatus(SHEET_KEYS.filingPrerequisites);
    const role0Data = this.stateService.getSheetData(SHEET_KEYS.filingPrerequisites);
    const hasData = role0Data && Object.keys(role0Data).length > 0;
    return !sheetLevelStatus.hasErrors && hasData;
  }

  /**
   * Check if this is first conversion based on original state from state service
   * First conversion = filingInfo & filingPrerequisites have data, but all other roles are empty
   * Used to determine whether to show/hide fields with hideAfterFirstConversion flag
   * Also used to determine if Submit button should be enabled without changes
   *
   * IMPORTANT: Uses state service's in-memory state instead of localStorage directly
   * This ensures correct timing when navigating between pages
   */
  private checkIfFirstConversion(): void {
    // If no conversionId, this is a new conversion (first conversion)
    if (!this.conversionId) {
      this.isFirstConversion = true;
      return;
    }

    // Get the original state from state service (in-memory, more reliable timing)
    const originalState = this.stateService.getOriginalState();

    // Check if original state is empty (no data loaded yet)
    const hasAnyData = Object.values(originalState).some(
      sheetData => sheetData && Object.keys(sheetData).length > 0
    );

    if (!hasAnyData) {
      // No original state data found yet, treat as first conversion
      this.isFirstConversion = true;
      return;
    }

    // Keys that are allowed to have data in first conversion
    const allowedKeysWithData = ['filingInfo', 'filingPrerequisites'];

    // Check if all other keys (besides filingInfo and filingPrerequisites) are empty
    let hasDataInOtherRoles = false;

    Object.keys(originalState).forEach((key) => {
      // Skip the allowed keys
      if (allowedKeysWithData.includes(key)) {
        return;
      }

      // Check if this key has non-empty data
      const keyData = (originalState as any)[key];
      if (keyData && typeof keyData === 'object' && Object.keys(keyData).length > 0) {
        hasDataInOtherRoles = true;
      }
    });

    // First conversion if filingInfo/filingPrerequisites have data but other roles are empty
    this.isFirstConversion = !hasDataInOtherRoles;
  }

  /**
   * Show edit warning dialog and clear all other roles if user confirms
   * Called when user tries to re-submit Role-0 after initial submission
   */
  private showEditWarningAndClearData(): void {
    if (this.hasShownEditWarning) {
      return; // Only show once per session
    }

    this.hasShownEditWarning = true;
    this.isEditWarningDialogOpen.set(true);
  }

  /**
   * Handle Convert button click in edit warning dialog
   */
  onEditWarningConvert(): void {
    this.isEditWarningDialogOpen.set(false);

    // NOTE: the other roles' data is NOT cleared here. `clearAllOtherRolesData()`
    // is destructive and irreversible (it wipes both baselines AND persisted
    // storage), so running it before the request meant a failed convert —
    // gateway error, timeout, 4xx — destroyed the user's other sheets for a
    // submission that never happened. It now runs from onSubmitSucceeded(),
    // once the backend has confirmed it persisted the submission.
    //
    // Commit is likewise deferred to onSubmitSucceeded() (container callback on
    // a persisted response) — same as the direct onSubmit() path — so a failed
    // convert leaves the form dirty and retryable.
    const formData = this.filingForm.getRawValue();
    this.isSubmitting = true;
    this.submitForm.emit(formData);
  }

  /**
   * Handle Back button click in edit warning dialog
   */
  onEditWarningBack(): void {
    this.isEditWarningDialogOpen.set(false);

    // Reset warning flag to allow it to show again on next submit attempt
    this.hasShownEditWarning = false;
  }

  /**
   * Toggle edit warning dialog visibility
   */
  toggleEditWarningDialog(isOpen: boolean): void {
    this.isEditWarningDialogOpen.set(isOpen);
    if (!isOpen) {
      // Reset warning flag when dialog is closed
      this.hasShownEditWarning = false;
    }
  }

  /**
   * Clear all data from other roles (keep only Role-0)
   */
  /**
   * Wipe every sheet except Role-0, keeping the Role-0 slice intact.
   *
   * DESTRUCTIVE AND IRREVERSIBLE: `clearAllData()` resets BOTH the original and
   * modified baselines and clears the per-conversion persisted storage. Call it
   * ONLY after the backend has confirmed the submission persisted (see
   * `onSubmitSucceeded`) — the submitted conversion is re-extracted end to end,
   * so the other sheets are about to be replaced wholesale, but a submit that
   * FAILED must leave the user's data exactly where it was.
   */
  private clearAllOtherRolesData(): void {
    const currentState = this.stateService.getState();
    const role0Data = { ...currentState.filingPrerequisites }; // Keep Role-0 data

    // Clear all roles except filingPrerequisites
    this.stateService.clearAllData();

    // Restore Role-0 data
    this.stateService.setSheetData(SHEET_KEYS.filingPrerequisites, role0Data);
  }

  /**
   * Submit Role-0 form
   * Emits form data to parent (filing-form-container) for processing
   * CRITICAL: Shows edit warning if Role-0 was previously submitted
   * NOTE: For first conversion, allow submission without changes (a form with
   * no changes yet is OK when this is the only filing sheet)
   */
  onSubmit(): void {
    // SILENT defence-in-depth. The Submit button is the user-facing signal:
    // it grays out whenever the form is invalid, and whenever nothing has
    // changed on a sheet that is not the only filing sheet. These guards exist only so
    // a non-button caller (dialog, spec, future shortcut) cannot emit an
    // invalid or no-op payload — they deliberately raise NO toast, because a
    // toast can only ever fire on a path the disabled button already blocks.
    //
    // The conditions MUST mirror `[submitDisabled]` in the template exactly
    // (see role0-000000-filing-prerequisites.component.html). Guarding on
    // `isFirstConversion` here while the template waived the changes-term on
    // `isOnlyFilingSheet` was a drift that produced an enabled button whose
    // click was silently swallowed.
    //
    // The changes-term is the modified-vs-original verdict, NOT
    // `filingForm.pristine` — identical to role1, so both sheets measure
    // "changed" the same way. The `isOnlyFilingSheet` waiver is the ONLY
    // remaining divergence: a fresh Phase 1 conversion is valid-by-defaults and
    // has no changes yet, and must still be submittable.
    if (!this.hasUnsubmittedChanges() && !this.isOnlyFilingSheet) {
      return;
    }

    if (!this.filingForm.valid) {
      this.filingForm.markAllAsTouched();
      return;
    }

    // Always route Submit through the confirmation dialog — first-time AND
    // re-submission. The backend request is fired only from the dialog's
    // Convert button (onEditWarningConvert), so nothing is submitted (and no
    // toast shows) until the user confirms in the pop-up. `hasShownEditWarning`
    // is re-armed after each submit outcome (onSubmitSucceeded/onSubmitFailed)
    // and on Back, so the dialog re-appears on the next Submit.
    if (!this.hasShownEditWarning) {
      this.showEditWarningAndClearData();
      return; // Don't submit until user confirms via dialog
    }

    // Get form data (including disabled fields)
    const formData = this.filingForm.getRawValue();

    // Disable the button while the container's HTTP call is in flight, then
    // emit for processing. The pristine/commit/completed bookkeeping is now
    // DEFERRED to onSubmitSucceeded() (called back by the container ONLY on a
    // persisted response) so a failed submit leaves the form dirty and the
    // Submit button re-enabled for retry — mirroring Submit-for-Validation.
    this.isSubmitting = true;
    this.submitForm.emit(formData);
  }

  /**
   * Container callback: the phase-two submit persisted (HTTP 200, status true).
   * Only now do we commit local state and re-arm the edit-warning cycle.
   */
  onSubmitSucceeded(): void {
    this.isSubmitting = false;

    // Mark form as pristine after submission (disables Submit button until next change)
    this.filingForm.markAsPristine();

    // Now — and only now — discard the other sheets. The backend has persisted
    // the submission and will re-extract every sheet, so their local copies are
    // stale. Deliberately BEFORE the commit below: clearAllOtherRolesData()
    // re-seeds Role-0 into the MODIFIED state only, and the commit that follows
    // promotes it to original, leaving every sheet consistent.
    this.clearAllOtherRolesData();

    // CRITICAL: Commit modified state as new original (prevents false "modified" detection)
    this.stateService.commitSheetAsOriginal(SHEET_KEYS.filingPrerequisites);

    // Mark as initially completed (enables warning on future re-submissions)
    this.wasInitiallyCompleted = true;
    this.hasShownEditWarning = false; // Reset warning flag for next edit cycle
  }

  /**
   * Container callback: the phase-two submit failed (non-200 / transport fault).
   * Stay on the form, re-enable the Submit button, do NOT commit local state.
   */
  onSubmitFailed(): void {
    this.isSubmitting = false;
    this.hasShownEditWarning = false; // Allow the edit-warning dialog to show again on retry
  }

  /**
   * Handle Delete button click
   * Opens the delete-conversion dialog
   */
  onDelete(): void {
    this.isArchiveDialogOpen.set(true);
  }

  /**
   * Handle deletion completion from delete-conversion component
   * Redirects to XBRL files page
   */
  onDeletionCompleted(): void {
    // Clear local storage data
    this.stateService.clearAllData();

    // Redirect to XBRL files page with toast message
    this.router.navigate(['/mbrs/xbrl-files'], {
      state: {
        toast: {
          key: 'process_mbrs_data',
          severity: 'custom-success',
          summary: 'Conversion deleted successfully!',
          life: TOAST_DURATION_LONG_MS,
        },
      },
    });
  }

  /**
   * Get the DeleteConversionTarget for delete-conversion component
   * Creates a minimal conversion description from available inputs
   */
  get conversionData(): DeleteConversionTarget {
    return {
      id: this.conversionId || '',
      fileName: '',
      company: this.companyName || '',
      auditYear: this.auditYear?.toString() || '',
      status: this.conversionStatus ?? MbrsStatusEnum.InProgress,
      // Real values from the audit read, or undefined when none answered: the
      // dialog then shows "-" rather than an invented "now".
      conversionDate: this.conversionAudit?.conversionDate,
      updatedDate: this.conversionAudit?.updatedDate,
      createdBy: this.conversionAudit?.createdBy,
      updatedBy: this.conversionAudit?.updatedBy,
      fileUrl: '',
      mbrsData: [],
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   *
   * CRITICAL FIX: Changed from state$ to originalState$ to avoid feedback loop
   * - state$ (modifiedState$) changes on every autosave → would mark form pristine → BUG
   * - originalState$ only changes when API data loads → correct behavior
   */
  private setupStateListener(): void {
    this.stateService.originalState$ // ← Changed from state$ to originalState$
      .pipe(
        map((state) => state.filingPrerequisites),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ), // Only react to actual changes
        takeUntil(this.destroy$)
      )
      .subscribe((filingData) => {
        if (filingData && Object.keys(filingData).length > 0) {
          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // Convert date strings to Date objects (same as loadSavedState does)
          const convertedData =
            this.convertDateStringsToDateObjects(filingData);

          // Preserve default values for readonly fields that are empty in external data
          const finalData = { ...convertedData };
          this.schema.controls.forEach(control => {
            if (control.ui.readonly && control.ui.defaultValue) {
              // Only use default value if API value is empty/undefined
              // This prevents overwriting valid API values (e.g., TypeOfSubmission from backend)
              const apiValue = finalData[control.qname];
              if (apiValue === undefined || apiValue === null || apiValue === '') {
                finalData[control.qname] = control.ui.defaultValue;
              }
            }
          });

          // Patch the form with the new data
          this.filingForm.patchValue(finalData, { emitEvent: false });

          // Mark form as pristine because this is external API data (not user changes)
          this.filingForm.markAsPristine();

          // Re-apply conditional-required rules for the JUST-HYDRATED trigger
          // values. The patchValue above used `emitEvent: false`, so the
          // valueChanges-driven wiring in setupConditionalValidation never saw
          // the backend value — without this, a conversion that already had
          // "Subsequent preparation of financial statements" set would load
          // with its previous-year date fields un-required (no error, no submit
          // block). See reapplyConditionalValidation() for the full rationale.
          this.reapplyConditionalValidation();

          // Re-apply the reactive-rule engines for the JUST-HYDRATED values.
          // Same rationale as reapplyConditionalValidation above, one rule
          // family wider: the patch used `emitEvent: false`, so disabledWhen /
          // lockWhen / optionsFilteredBy / TypeOfSubmission / BankNegara /
          // method-fields never saw the backend value. Without this, an
          // extracted conversion renders enable/disable, hidden and filtered-
          // option state computed from PRE-hydration values.
          this.reapplyReactiveRules();

          // NOTE: kept SYNCHRONOUS (no setTimeout) — mirrors role1's
          // identical listener (role1-010000-filing-info.component.ts,
          // "kept SYNCHRONOUS (no setTimeout)"). The 100ms delay this used to
          // sit behind existed to let a stray autosave settle before
          // re-enabling it; after Task 2, every write on this hydration path
          // (reapplyConditionalValidation, reapplyReactiveRules, and the
          // lock-rule's own SYSTEM re-apply) uses `emitEvent: false`, so
          // `setupStatePersistence`'s debounced pipeline never wakes as a
          // side effect of hydration — there is nothing left to outrun, so
          // there is nothing to delay for.
          //
          // This listener does NOT finalize the sheet: it never calls
          // `markSheetInitialized`, `markSheetInitializedWithStatus`, or
          // `markSheetInitializing`. It must not start — finalization is
          // owned by `finalizeInit()`.
          this.isLoadingState = false;

          // Mark invalid controls as touched so validation errors and red borders show on initial load
          this.touchInvalidControls();

          // CRITICAL: Re-check isFirstConversion after API data is loaded to localStorage
          // This fixes the issue where navigation from another page runs checkIfFirstConversion()
          // before the original state is populated in localStorage
          this.checkIfFirstConversion();
        }
      });
  }

  /**
   * Load the form schema from JSON
   */
  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-0.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Load type dictionary for dropdown options
   */
  private async loadTypeDict(): Promise<void> {
    // FATAL by design: without the dictionary the dropdowns have no options and
    // the conditional mappings cannot run, so the sheet must not open as if it had
    // loaded. The typed error propagates to the sheet's single catch, which
    // reports ASSET / PARSE / NET through the load trace.
    this.typeDict = await fetchJsonAsset<any>('/assets/types.dict.json');
    this.buildDropdownOptions();
  }

  /**
   * Build dropdown options from type dictionary
   */
  private buildDropdownOptions(): void {
    if (!this.typeDict.enums) return;

    Object.keys(this.typeDict.enums).forEach((key) => {
      this.dropdownOptions[key] = this.typeDict.enums[key].map(
        (value: string) => ({
          label: value,
          value: value,
        })
      );
    });
  }

  /**
   * Build the reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }

    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      const validators = [];

      // Add required validator. Unconditionally required when
      // `control.required === true` OR an unconditional
      // `validators:[{name:'required'}]` entry is present (and it is not a
      // conditional-only required). Shared derivation keeps role0 aligned with
      // role1 and both pure validators — previously role0 only honoured the
      // top-level flag and silently dropped the validators-array form (e.g.
      // ssmt-dei:DescriptionOfFunctionalCurrency). See filing-info-common.rules.
      if (isUnconditionallyRequired(control)) {
        validators.push(Validators.required);
      }

      // Add pattern validator if specified
      const patternValidator = control.validators?.find(
        (validator) => validator.name === 'pattern'
      );
      if (patternValidator && patternValidator.patternSource) {
        const patternKey = patternValidator.patternSource.replace('type:', '');
        const pattern = this.typeDict.patterns?.[patternKey];
        if (pattern) {
          validators.push(Validators.pattern(pattern));
        }
      }

      // Get default value
      let defaultValue = '';
      if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // Create form control
      const formControl = new FormControl(
        { value: defaultValue, disabled: control.ui.readonly || false },
        validators
      );

      formControls[control.qname] = formControl;
    });

    this.filingForm = new FormGroup(formControls);
  }

  /**
   * Load saved state from state service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.filingPrerequisites);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.filingPrerequisites);

    if (savedData && Object.keys(savedData).length > 0) {

      // Convert date strings back to Date objects for date picker fields
      const convertedData = this.convertDateStringsToDateObjects(savedData);

      // Preserve default values for readonly fields that are empty in saved data
      const finalData = { ...convertedData };
      this.schema.controls.forEach(control => {
        if (control.ui.readonly && control.ui.defaultValue) {
          // Only use default value if saved value is empty/undefined
          // This prevents overwriting valid saved values (e.g., TypeOfSubmission)
          const savedValue = finalData[control.qname];
          if (savedValue === undefined || savedValue === null || savedValue === '') {
            finalData[control.qname] = control.ui.defaultValue;
          }
        }
      });

      this.filingForm.patchValue(finalData, { emitEvent: false });

      // Mark form as pristine so Submit button is disabled until user makes changes
      this.filingForm.markAsPristine();
    } else {
      // No saved data - ensure default values are set for readonly fields
      const defaultData: Record<string, any> = {};
      this.schema.controls.forEach(control => {
        if (control.ui.readonly && control.ui.defaultValue) {
          defaultData[control.qname] = control.ui.defaultValue;
        }
      });
      this.filingForm.patchValue(defaultData, { emitEvent: false });
    }
  }

  /**
   * Deterministic init finalization. Replaces the legacy
   * setTimeout(100) -> setTimeout(600) chain that used to live in
   * loadSavedState().
   *
   * Must run after EVERY loadSavedState() — init AND cancel/revert —
   * because loadSavedState opens the bracket with markSheetInitializing()
   * and only this closes it. A sheet left inside initializingSheets never
   * reports status.
   *
   * The 600ms inner timer used to exist so the debounced persist
   * could land the lock-corrected Basis before the baseline snapshot. Item
   * A gave applyLockRule its own syncFormToState(), so there is nothing
   * left to outrun and no `awaitSettled` is needed (TIER-A).
   *
   * The touch loop runs AFTER the helper: it marks invalid controls
   * touched so red borders show, and `touched` does not affect
   * `form.valid`, which is all markSheetInitializedWithStatus reads. Same
   * post-step pattern role4 uses.
   */
  private async finalizeInit(): Promise<void> {
    await finalizeSheetInit({
      trace: this.loadTraceHandle,
      stateService: this.stateService,
      sheetKey: SHEET_KEYS.filingPrerequisites,
      form: this.filingForm,
      setLoadingState: (isLoading) => { this.isLoadingState = isLoading; },
      isComputed: computedControlPredicate(this.schema?.controls),
      persistInitial: () => {
        this.stateService.updateSheetData(
          SHEET_KEYS.filingPrerequisites,
          this.filingForm.getRawValue(),
          this.filingForm.valid,
          true, // skipEditedMark — re-baseline, not a user edit
        );
      },
    });

    this.touchInvalidControls();

    // The initializing bracket has just closed, so isSheetModified() can now
    // answer truthfully — see refreshHasUnsubmittedChanges().
    this.refreshHasUnsubmittedChanges();
  }

  /**
   * Recompute the changes-term from state. Must ALSO be called from
   * `finalizeInit()`, not only from the state subscription: `isSheetModified`
   * returns false for a sheet inside the `initializingSheets` bracket, and
   * `finalizeSheetInit` closes that bracket WITHOUT emitting on
   * `modifiedState$`.
   */
  private refreshHasUnsubmittedChanges(): void {
    this.hasUnsubmittedChanges.set(
      this.stateService.isSheetModified(SHEET_KEYS.filingPrerequisites)
    );
  }

  /**
   * Keep `hasUnsubmittedChanges` in step with the dual-state store — both
   * subjects, because both move it (`modifiedState$` = the debounced autosave,
   * `originalState$` = `commitSheetAsOriginal()` on a successful submit).
   */
  private setupUnsubmittedChangesTracking(): void {
    combineLatest([this.stateService.modifiedState$, this.stateService.originalState$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshHasUnsubmittedChanges());
  }

  /** Mark invalid, enabled controls touched so validation errors paint. */
  private touchInvalidControls(): void {
    Object.keys(this.filingForm.controls).forEach((key) => {
      const control = this.filingForm.get(key);
      if (control && control.invalid && !control.disabled) {
        control.markAsTouched();
      }
    });
  }

  /**
   * Convert date strings from persisted/API state back to Date objects.
   *
   * Delegates to the shared `coerceSchemaDateStrings` — see that util's
   * docblock for why an unconverted string renders as a BLANK
   * `<p-datepicker>`, and for which state slice carries which shape. The
   * other filing-info sheets carry the identical one-liner; keep them in step.
   */
  private convertDateStringsToDateObjects(
    data: Record<string, any>
  ): Record<string, any> {
    return coerceSchemaDateStrings(data, this.schema?.controls);
  }

  /**
   * Setup auto-save on form changes
   * Only saves to localStorage when form is VALID
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.filingForm.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        // Normalise the form BEFORE reading its value or validity. The state
        // seam trims the BRN on write, so without this the form would keep the
        // untrimmed value: `isValid` (and the Submit gate) would be computed
        // against a 12-digit value that passes the pattern, while an 11-digit
        // value went to the backend — surfacing the "12 digit" error only on
        // the next visit. See applyTrimNormalizersToForm.
        applyTrimNormalizersToForm(this.filingForm);

        const formValue = this.filingForm.getRawValue();
        const isValid = this.filingForm.valid;
        this.stateService.updateSheetData(SHEET_KEYS.filingPrerequisites, formValue, isValid);
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.filingPrerequisites, { hasErrors: !this.filingForm.valid });
      },
    });
  }

  /**
   * Setup warning evaluation for data quality suggestions
   * Warnings only appear in Edited Mode (when conversionId exists)
   */
  private setupWarningEvaluation(): void {
    // Layer 1: local valueChanges — debounced re-evaluation when user edits.
    this.filingForm.valueChanges
      .pipe(
        debounceTime(FORM_AUTOSAVE_DEBOUNCE_MS),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.runWarningEvaluation();
      });

    // Layer 2: cross-tab live adoption — when a sibling tab edits Phase 2's
    // filingPrerequisites slice, `applyRemoteChange` rehydrates the shared
    // state and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges), which is what makes Layer 1's
    // local valueChanges-driven re-evaluation moot here — the field-level
    // warning indicators are refreshed as part of adoption instead. Mirrors
    // role1's Layer 2 (which pins filingInfo); here streamKey is
    // 'modifiedState' and changedSheets carries filingPrerequisites.
    // `sheetKey` is a hint the save funnels never populate, so filtering on
    // it meant this subscription never fired. `changedSheets` is diff-derived
    // and authoritative.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.filingPrerequisites)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.filingPrerequisites)),
        filter(() => !!this.schema && !!this.filingForm),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role0-filingPrerequisites',
          // });
        }
        this.adoptRemoteState();
      });
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    // Date fields must be coerced BEFORE the patch, exactly as `loadSavedState()`
    // and the sheet's own API-hydration listener already do. A sibling's
    // DISCARD republishes the ORIGINAL slice — which carries the API's raw
    // "dd/MM/yyyy" strings, not the `Date` objects the modified slice holds —
    // so adopting it verbatim wrote a string into `<p-datepicker>`, which
    // renders an unparseable value as an EMPTY field. Reported on role1's
    // two "Company previous financial year" dates; role0 shares those fields
    // and the seam, per the standing role0/role1 parity rule. Pinned by
    // `cross-tab-adoption-date-coercion.spec.ts`.
    adoptIntoAutoSaveSheet(
      this.filingForm,
      this.convertDateStringsToDateObjects(
        this.stateService.getSheetData(SHEET_KEYS.filingPrerequisites)
      )
    );
    this.runWarningEvaluation();
  }

  /**
   * Pure-refactor extraction of the previous inline warning-evaluation
   * subscription body. Re-used by both the local valueChanges subscription
   * and the cross-tab remoteUpdateApplied$ subscription so the field-level
   * warning indicators stay accurate on both code paths.
   *
   * Behaviour-preserving — semantics are identical to the original inline
   * implementation; only the call-site is now reusable.
   */
  private runWarningEvaluation(): void {
    // Only evaluate warnings in edited mode
    if (!this.isEditedMode()) {
      this.fieldWarnings = {};
      return;
    }

    // Get all form values for cross-field validation
    const allFormValues = this.filingForm.getRawValue();

    // Evaluate warnings for each field that has warning rules
    this.schema.controls.forEach((control) => {
      if (control.warnings && control.warnings.length > 0) {
        const formControl = this.filingForm.get(control.qname) as FormControl;
        if (formControl) {
          const warnings = this.warningService.evaluateWarnings(
            formControl,
            control.warnings,
            allFormValues,
            this.isEditedMode()
          );

          if (warnings.length > 0) {
            this.fieldWarnings[control.qname] = warnings;
          } else {
            // Clear warnings if none triggered
            delete this.fieldWarnings[control.qname];
          }
        }
      }
    });
  }

  /**
   * Setup conditional field validation by iterating the shared
   * FILING_INFO_CONDITIONAL_RULES table. Both this method and the pure
   * role0FilingPrerequisitesValidator consume the same rule list so
   * adding/changing a conditional rule means editing one place.
   *
   * Per rule: read the trigger control's current value, drive the affected
   * fields' required state, then subscribe to valueChanges to repeat on
   * every update.
   */
  private setupConditionalValidation(): void {
    for (const rule of FILING_INFO_CONDITIONAL_RULES) {
      const triggerControl = this.filingForm.get(rule.trigger);
      if (!triggerControl) continue;

      const applyRule = (value: unknown) => {
        const matched = rule.whenAnyOf.includes(value as string);
        for (const requireField of rule.requireFields) {
          this.updateConditionalField(requireField, matched);
        }
      };

      applyRule(triggerControl.value);
      triggerControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => applyRule(value));
    }
  }

  /**
   * Re-apply every conditional-required rule against its trigger's CURRENT
   * value.
   *
   * Why this exists separately from setupConditionalValidation's mount-time
   * pass: backend hydration flows through the `originalState$` listener, which
   * patches the trigger field with `patchValue(..., { emitEvent: false })`.
   * That suppresses `valueChanges`, so the per-trigger subscription above never
   * sees the hydrated value, and setupConditionalValidation's one-shot
   * `applyRule(triggerControl.value)` already ran (before the API data arrived)
   * against the empty/stale value. The net effect was a regression: a
   * conversion that already had "Subsequent preparation of financial
   * statements" persisted on the backend would load with its previous-year
   * date fields NOT marked required — no error, no submission block — until the
   * user manually toggled the dropdown. Calling this right after the listener's
   * patch re-attaches Validators.required for the hydrated trigger value so the
   * subsequent touch-loop surfaces the empty-required errors immediately.
   */
  private reapplyConditionalValidation(): void {
    for (const rule of FILING_INFO_CONDITIONAL_RULES) {
      const triggerControl = this.filingForm.get(rule.trigger);
      if (!triggerControl) continue;

      const matched = rule.whenAnyOf.includes(triggerControl.value as string);
      for (const requireField of rule.requireFields) {
        this.updateConditionalField(requireField, matched);
      }
    }
  }

  /**
   * Re-apply every reactive-rule engine against the form's CURRENT values.
   *
   * Appliers run in registration order, which is `ngOnInit` order, which
   * encodes the dependency chain — `setupLockWhenRules` registers before
   * `setupTypeOfSubmissionAutoSet` because the Origin→Basis lock must settle
   * before the derived TypeOfSubmission is computed from Basis; and
   * `setupTypeOfSubmissionAutoSet` registers before `setupMethodFieldsManagement`
   * because the latter's composite key reads `ssmt-dei:TypeOfSubmission`.
   * Preserve that order (matches role1's identical mechanism).
   *
   * Every applier writes with `emitEvent: false`, so one pass cannot cascade
   * into the autosave pipeline or mark the form dirty.
   */
  private reapplyReactiveRules(): void {
    this.reactiveRuleAppliers.forEach((apply) => apply());
  }

  /**
   * Update conditional field validation
   */
  private updateConditionalField(
    fieldName: string,
    shouldBeRequired: boolean
  ): void {
    const control = this.filingForm.get(fieldName);
    if (control) {
      if (shouldBeRequired) {
        control.setValidators([Validators.required]);
        // Mark as touched so validation errors show immediately if field is empty
        control.markAsTouched();
      } else {
        control.clearValidators();
        control.setValue('', { emitEvent: false });
      }
      control.updateValueAndValidity({ emitEvent: false });

      // CRITICAL: Re-add custom date validators after conditional validation changes
      // This is needed for previous financial year date fields
      this.reapplyDateValidatorsIfNeeded(fieldName);

      // The clear branch above writes with `emitEvent: false` (deliberately —
      // see the comment there), so the cross-field subscription wired in
      // setupFinancialYearDateValidation() never wakes. Without this call the
      // SIBLING date controls keep verdicts computed against the value we just
      // wiped: switching "Disclosure of financial statements preparation for
      // current submission" from Subsequent to First time blanked both
      // previous-year dates but left `dateSequenceInvalid` on Company CURRENT
      // financial year start date — an unclearable error that held the sheet
      // at ✗ and the submit gate closed. Re-validating all four is silent
      // (`emitEvent: false`), so the autosave/pencil contract is untouched.
      this.revalidateFinancialYearDates();
    }
  }

  /**
   * Re-run validity on all four financial-year date controls.
   *
   * The three date rules (current range, previous range, previous-end <
   * current-start) are CROSS-FIELD: each is attached to more than one control,
   * so a change to any one date can only settle the others if every one of the
   * four is re-validated. This is the single place that does it — called by
   * the `valueChanges` subscription in setupFinancialYearDateValidation() (a
   * user edit), by its initial pass (mount), and by updateConditionalField()
   * (a silent conditional clear, which reaches no subscription at all).
   *
   * Always silent: `emitEvent: false` on every control, so it can never wake
   * the autosave pipeline or mark the sheet edited.
   */
  private revalidateFinancialYearDates(): void {
    for (const key of [
      FILING_INFO_FIELD_KEYS.currentYearStart,
      FILING_INFO_FIELD_KEYS.currentYearEnd,
      FILING_INFO_FIELD_KEYS.previousYearStart,
      FILING_INFO_FIELD_KEYS.previousYearEnd,
    ]) {
      this.filingForm.get(key)?.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Re-apply custom date validators to financial year fields
   * Called after conditional validation updates to restore cross-field date validators
   */
  private reapplyDateValidatorsIfNeeded(fieldName: string): void {
    const control = this.filingForm.get(fieldName);
    if (!control) return;

    // Get the stored validators for this field
    const customValidators = this.dateValidators[fieldName];
    if (!customValidators || customValidators.length === 0) return;

    // Get current validators (should be just [Validators.required] or empty)
    const hasRequired = control.hasValidator(Validators.required);

    // Combine required (if present) with custom validators
    const allValidators = hasRequired
      ? [Validators.required, ...customValidators]
      : customValidators;

    control.setValidators(allValidators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Earliest selectable day on the "Company current financial year end date"
   * picker, and the copy shown when the user hovers an earlier one. Exposed as
   * fields (not getters) so the template binds a stable reference.
   */
  readonly minSupportedFinancialYearEnd = MIN_SUPPORTED_CURRENT_FINANCIAL_YEAR_END;
  readonly unsupportedFinancialYearEndMessage =
    UNSUPPORTED_CURRENT_FINANCIAL_YEAR_END_MESSAGE;

  /**
   * `[minDate]` for a date control — the 2017-01-31 floor for the current
   * financial year END date, `undefined` (no floor) for every other date
   * field on this sheet.
   *
   * Scoped by qname on purpose: the start dates and both previous-year dates
   * legitimately precede the floor, and only the END date carries SSM's
   * constraint. See MIN_SUPPORTED_CURRENT_FINANCIAL_YEAR_END.
   */
  getDateFloor(control: { qname: string }): Date | undefined {
    return control.qname === FILING_INFO_FIELD_KEYS.currentYearEnd
      ? this.minSupportedFinancialYearEnd
      : undefined;
  }

  /**
   * True when a greyed-out calendar cell is greyed out BECAUSE of the
   * unsupported-period floor, so the tooltip explains that specific reason
   * rather than appearing on any disabled day.
   *
   * `dayMeta` is PrimeNG's day descriptor: `month` is 0-based.
   */
  isUnsupportedFinancialYearEndDay(dayMeta: {
    day: number;
    month: number;
    year: number;
  }): boolean {
    return isBeforeSupportedFinancialYearEnd(
      new Date(dayMeta.year, dayMeta.month, dayMeta.day)
    );
  }

  /**
   * Setup cross-field validation for financial year date fields
   * Validates 3 rules:
   * 1. Current Start <= Current End
   * 2. Previous Start <= Previous End
   * 3. Previous End < Current Start
   *
   * Uses Custom Validator Functions to persist errors across value changes
   */
  private setupFinancialYearDateValidation(): void {
    const currentStartControl = this.filingForm.get(FILING_INFO_FIELD_KEYS.currentYearStart);
    const currentEndControl = this.filingForm.get(FILING_INFO_FIELD_KEYS.currentYearEnd);
    const previousStartControl = this.filingForm.get(FILING_INFO_FIELD_KEYS.previousYearStart);
    const previousEndControl = this.filingForm.get(FILING_INFO_FIELD_KEYS.previousYearEnd);

    if (!currentStartControl || !currentEndControl || !previousStartControl || !previousEndControl) {
      console.warn('One or more financial year date controls not found');
      return;
    }

    // Wrap the shared check* primitives as Angular ValidatorFn instances.
    // The pure role0FilingPrerequisitesValidator calls the same primitives
    // against a flat sheetData snapshot, so error messages and trigger
    // conditions stay aligned without manual sync.
    const currentRangeValidator = () => {
      const message = checkCurrentRange(currentStartControl.value, currentEndControl.value);
      return message ? { dateRangeInvalid: message } : null;
    };

    const previousRangeValidator = () => {
      const message = checkPreviousRange(previousStartControl.value, previousEndControl.value);
      return message ? { dateRangeInvalid: message } : null;
    };

    const dateSequenceValidator = () => {
      const message = checkDateSequence(previousEndControl.value, currentStartControl.value);
      return message ? { dateSequenceInvalid: message } : null;
    };

    // Floor on the CURRENT year end only. The calendar picker already greys
    // out anything before the floor via [minDate], but the field also accepts
    // a TYPED date (see the (onInput) hook on the template) which minDate does
    // not intercept — so the constraint has to exist as a validator too.
    const currentEndFloorValidator = () => {
      const message = checkCurrentEndFloor(currentEndControl.value);
      return message ? { financialYearEndUnsupported: message } : null;
    };

    // Store validators for later reuse (when conditional validation changes)
    this.dateValidators[FILING_INFO_FIELD_KEYS.currentYearStart] = [currentRangeValidator, dateSequenceValidator];
    this.dateValidators[FILING_INFO_FIELD_KEYS.currentYearEnd] = [currentRangeValidator, currentEndFloorValidator];
    this.dateValidators[FILING_INFO_FIELD_KEYS.previousYearStart] = [previousRangeValidator];
    this.dateValidators[FILING_INFO_FIELD_KEYS.previousYearEnd] = [previousRangeValidator, dateSequenceValidator];

    // Add validators to controls
    currentStartControl.addValidators([currentRangeValidator, dateSequenceValidator]);
    currentEndControl.addValidators([currentRangeValidator, currentEndFloorValidator]);
    previousStartControl.addValidators([previousRangeValidator]);
    previousEndControl.addValidators([previousRangeValidator, dateSequenceValidator]);

    // Mark controls as touched to show errors immediately
    const markTouchedIfInvalid = (control: any) => {
      if (control.invalid) {
        control.markAsTouched({ onlySelf: true });
      }
    };

    // Watch for changes in any of the 4 date fields and re-validate all affected controls
    [currentStartControl, currentEndControl, previousStartControl, previousEndControl].forEach(control => {
      control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          // When any date changes, update validity of all 4 controls (cross-field dependencies)
          this.revalidateFinancialYearDates();

          // Mark controls as touched if they become invalid
          markTouchedIfInvalid(currentStartControl);
          markTouchedIfInvalid(currentEndControl);
          markTouchedIfInvalid(previousStartControl);
          markTouchedIfInvalid(previousEndControl);
        });
    });

    // Run initial validation
    this.revalidateFinancialYearDates();
  }

  /**
   * Setup field disabling based on schema disabledWhen configuration
   */
  private setupFieldDisabling(): void {
    // Find all fields with disabledWhen configuration
    const fieldsWithDisabling = this.schema.controls.filter(
      (control) => control.ui.disabledWhen
    );

    fieldsWithDisabling.forEach((fieldSchema) => {
      const {
        field: triggerField,
        values: disablingValues,
        setValueTo,
        hideWhenDisabled,
      } = fieldSchema.ui.disabledWhen!;
      const targetControl = this.filingForm.get(fieldSchema.qname);
      const triggerControl = this.filingForm.get(triggerField);

      if (!targetControl || !triggerControl) return;

      const applyDisableRule = (triggerValue: unknown) => {
        const shouldDisable = disablingValues.includes(triggerValue as string);

        if (shouldDisable) {
          targetControl.disable({ emitEvent: false });
          if (setValueTo !== undefined) {
            // Don't overwrite an API value with an empty string: if setValueTo
            // is '' and original state has a value, preserve the original.
            if (setValueTo === '') {
              const originalValue = this.stateService.getOriginalSheetData(
                SHEET_KEYS.filingPrerequisites
              )[fieldSchema.qname];
              targetControl.setValue(
                originalValue && originalValue !== '' ? originalValue : setValueTo,
                { emitEvent: false }
              );
            } else {
              targetControl.setValue(setValueTo, { emitEvent: false });
            }
          }
          if (hideWhenDisabled) this.hiddenFields[fieldSchema.qname] = true;
        } else {
          // Re-enable ONLY if nothing else still holds this control disabled.
          // `isHeldDisabledByAnotherRule` covers `readonly` (disabled by
          // construction in buildForm) AND an active `lockWhen` on the same
          // control — Basis is locked to MFRS by Origin, and this branch used
          // to hand it back to the user on any Types-of-company change.
          if (!isHeldDisabledByAnotherRule(fieldSchema, this.filingForm, 'disabledWhen')) {
            targetControl.enable({ emitEvent: false });
          }
          if (hideWhenDisabled) this.hiddenFields[fieldSchema.qname] = false;
        }
      };

      applyDisableRule(triggerControl.value);
      this.reactiveRuleAppliers.push(() => applyDisableRule(triggerControl.value));

      triggerControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => applyDisableRule(value));
    });
  }

  /**
   * Setup conditional dropdown options based on schema optionsFilteredBy configuration
   * Supports both single-field and multi-field filtering
   */
  private setupConditionalOptions(): void {
    // Find all fields with optionsFilteredBy configuration
    const fieldsWithFiltering = this.schema.controls.filter(
      (control) => control.ui.optionsFilteredBy
    );

    fieldsWithFiltering.forEach((fieldSchema) => {
      const filterConfig = fieldSchema.ui.optionsFilteredBy!;
      const {
        field: singleField,
        fields: multipleFields,
        mappingSource,
      } = filterConfig;

      // Determine if single-field or multi-field filtering
      const triggerFields =
        multipleFields || (singleField ? [singleField] : []);
      const isMultiField = !!multipleFields;

      if (triggerFields.length === 0) {
        console.error(
          'No trigger fields specified for conditional filtering:',
          fieldSchema.qname
        );
        return;
      }

      // Resolve mappings from typeDict or use inline mappings (backward compatibility)
      let mappings: { [key: string]: string[] };

      if (mappingSource) {
        // Load from typeDict conditionalMappings
        const mappingKey = mappingSource.replace('conditional:', '');
        const conditionalMapping =
          this.typeDict.conditionalMappings?.[mappingKey];

        if (!conditionalMapping || !conditionalMapping.mappings) {
          console.error(
            `Conditional mapping '${mappingKey}' not found in types.dict.json`
          );
          return;
        }

        mappings = conditionalMapping.mappings;
        if (isMultiField) {
        }
      } else if (filterConfig.mappings) {
        // Inline mappings (backward compatibility)
        mappings = filterConfig.mappings;
      } else {
        console.error(
          'No mappings configuration found for conditional field:',
          fieldSchema.qname
        );
        return;
      }

      const targetControl = this.filingForm.get(fieldSchema.qname);
      if (!targetControl) {
        console.warn(`Target control not found: ${fieldSchema.qname}`);
        return;
      }

      // Get all trigger controls
      const triggerControls = triggerFields
        .map((fieldName) => this.filingForm.get(fieldName))
        .filter((ctrl) => ctrl !== null);

      if (triggerControls.length === 0) {
        console.warn(
          `No valid trigger controls found for: ${fieldSchema.qname}`
        );
        return;
      }

      // Helper function to build composite key from multiple field values
      const buildCompositeKey = (): string => {
        if (isMultiField) {
          // Multi-field: Join values with pipe separator (e.g., "Public company|Malaysian Financial Reporting Standards")
          const values = triggerFields.map((fieldName) => {
            const control = this.filingForm.get(fieldName);
            return control?.value || '';
          });
          return values.join('|');
        } else {
          // Single-field: Use value directly
          return triggerControls[0]!.value || '';
        }
      };

      // De-duplicated union of every value across every mapping entry for
      // this control, preserving first-appearance order. Used as the
      // cleared-driver fallback below instead of the raw, unfiltered enum:
      // the raw enum can contain values (e.g. "Company limited by
      // guarantee" in TypeOfCompanyByOrigin) that are not reachable under
      // ANY supported driver value, and offering them lets the user select
      // a value whose own `disabledWhen` side-effects (e.g. locking Basis
      // to MFRS) were never meant to be user-triggerable. Falls back to
      // `undefined` (raw-enum behaviour) if the mapping cannot be resolved.
      const mappedUnionOptions: string[] | undefined = (() => {
        if (!mappings) return undefined;
        const seen = new Set<string>();
        const union: string[] = [];
        for (const values of Object.values(mappings)) {
          if (!Array.isArray(values)) continue;
          for (const value of values) {
            if (!seen.has(value)) {
              seen.add(value);
              union.push(value);
            }
          }
        }
        return union.length > 0 ? union : undefined;
      })();

      // Helper function to update filtered options based on composite key
      const updateFilteredOptions = (fromHydration = false) => {
        const compositeKey = buildCompositeKey();
        const allowedOptions = mappings[compositeKey];

        if (isMultiField) {
        }

        if (allowedOptions) {
          // Re-arm: a driver re-selected after an empty-driver hydration pass
          // must undo the disable + validator strip below. `mapSchemaValidators`
          // deliberately never adds `Validators.required` (see
          // schema-validators.util.ts), so it must be re-added here from the
          // same `isUnconditionallyRequired` source buildForm() uses — otherwise
          // a mandatory field re-arms with ZERO validators. The `disabledWhen`
          // exclusion prevents this re-arm from fighting setupFieldDisabling's
          // own applier, which registers earlier in the same re-apply pass.
          if (
            targetControl.disabled &&
            !fieldSchema.ui.readonly &&
            !fieldSchema.ui.disabledWhen
          ) {
            targetControl.enable({ emitEvent: false });
            const rearmed = [...mapSchemaValidators(fieldSchema as any)];
            if (isUnconditionallyRequired(fieldSchema as any)) {
              rearmed.unshift(Validators.required);
            }
            targetControl.setValidators(rearmed);
            targetControl.updateValueAndValidity({ emitEvent: false });
          }

          // Filter the dropdown options based on allowed values
          this.filteredDropdownOptions[fieldSchema.qname] = allowedOptions.map(
            (value) => ({
              label: value,
              value: value,
            })
          );

          // Clear the field value if it's not in the allowed options
          const currentValue = targetControl.value;
          if (currentValue && !allowedOptions.includes(currentValue)) {
            targetControl.setValue('', { emitEvent: false });
            // `emitEvent: false` keeps this off setupStatePersistence's
            // valueChanges pipeline, so the clear would otherwise never reach
            // modifiedStateSubject — which the pure validators read. role1
            // has always synced here; role0 did not, and the headless
            // verdict kept scoring the stale illegal value.
            this.syncFormToState();
          }
        } else if (fromHydration && !buildCompositeKey()) {
          // Extracted NULL driver: the legal domain is unknowable, so the
          // dependent renders empty + disabled + unvalidated and the mandatory
          // error surfaces on the DRIVER instead.
          //
          // Still gated on `fromHydration`, but no longer because the user
          // cascade is blocked — it shipped 2026-08-10. The two paths differ
          // deliberately: a USER clear leaves the dependent ENABLED and
          // required so they can re-pick immediately (setupDriverClearCascade),
          // whereas an extraction NULL disables it because no user action
          // implied a domain. This branch must still never fire from the
          // valueChanges subscriber below.
          //
          // EXCEPT for a `readonly` control, which is never user-pickable:
          // "the legal domain is unknowable so let the user re-pick once the
          // driver is known" has no meaning for a field the user can never
          // touch. Wiping one strips a schema `defaultValue` that nothing
          // will ever restore — the driver arriving later re-filters the
          // options but does not re-seat a default — leaving the field
          // permanently on "Please select" and submitting null. That is
          // exactly what happened to the stock-exchange disclosure once the
          // backend began returning null for it (the source AR/FS carries no
          // such field, so the LLM has nothing to map) AND for its
          // `ssmt-dei:TypeOfSubmission` driver. So: keep the current value,
          // falling back to the schema default when it is empty.
          if (fieldSchema.ui.readonly) {
            if (!targetControl.value && fieldSchema.ui.defaultValue) {
              targetControl.setValue(fieldSchema.ui.defaultValue, {
                emitEvent: false,
              });
            }
          } else {
            targetControl.setValue('', { emitEvent: false });
          }
          targetControl.disable({ emitEvent: false });
          targetControl.clearValidators();
          targetControl.updateValueAndValidity({ emitEvent: false });
          // A preserved readonly value needs its option to still exist:
          // `getDropdownOptions` returns this array whenever it is present
          // (an empty array is truthy), and `<p-select>` renders its
          // PLACEHOLDER — not the value — when the value matches no option.
          // Emptying the list here would therefore reproduce the very
          // "Please select" symptom this branch was just fixed to avoid, with
          // a correct value sitting behind it. Offer the union of every
          // reachable value, as the cleared-driver branch below does.
          this.filteredDropdownOptions[fieldSchema.qname] =
            fieldSchema.ui.readonly && mappedUnionOptions
              ? mappedUnionOptions.map((value) => ({ label: value, value }))
              : [];
          // Sync the cleared value into modifiedStateSubject. Without this,
          // the pure validator (which reads modifiedStateSubject) would keep
          // seeing the stale extracted value until an unrelated edit re-syncs
          // the form.
          this.syncFormToState();
        } else {
          // Cleared driver (or a driver value with no explicit mapping
          // entry): offer the union of every value that is reachable under
          // ANY supported driver value, not the raw unfiltered enum — see
          // `mappedUnionOptions` above for why. Fall back to the raw enum
          // only if the mapping itself could not be resolved into a union.
          if (mappedUnionOptions) {
            this.filteredDropdownOptions[fieldSchema.qname] =
              mappedUnionOptions.map((value) => ({ label: value, value }));
          } else {
            const enumKey =
              fieldSchema.ui.enumSource?.replace('type:', '') || '';
            this.filteredDropdownOptions[fieldSchema.qname] =
              this.dropdownOptions[enumKey] || [];
          }
        }
      };

      // Set initial filtered options
      updateFilteredOptions();
      this.reactiveRuleAppliers.push(() => updateFilteredOptions(true));

      // Watch for changes in ANY trigger field
      triggerControls.forEach((triggerControl, index) => {
        triggerControl!.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((value) => {
            updateFilteredOptions();
          });
      });
    });
  }

  /**
   * Setup Bank Negara Malaysia field auto-management based on Type of Submission
   */
  private setupBankNegaraFieldManagement(): void {
    // Find fields with autoManageByTypeOfSubmission flag
    const fieldsToAutoManage = this.schema.controls.filter(
      (control) => control.ui.autoManageByTypeOfSubmission
    );

    if (fieldsToAutoManage.length === 0) return;

    const typeOfSubmissionControl = this.filingForm.get(
      'ssmt-dei:TypeOfSubmission'
    );
    if (!typeOfSubmissionControl) {
      console.warn(
        'Type of Submission control not found for Bank Negara auto-management'
      );
      return;
    }

    fieldsToAutoManage.forEach((fieldSchema) => {
      const targetControl = this.filingForm.get(fieldSchema.qname);
      if (!targetControl) return;

      // Helper function to update visibility and value
      const updateFieldBasedOnTypeOfSubmission = (
        typeOfSubmissionValue: string | null
      ) => {
        if (!typeOfSubmissionValue || typeOfSubmissionValue === '') {
          // Hide field when Type of Submission is empty.
          //
          // It MUST be disabled, not enabled. This control is mandatory, and
          // an enabled control counts towards `filingForm.valid` even while
          // hidden — which is the Submit gate (see the SUBMIT GATE comment in
          // this component's template). Enabling it here therefore greys
          // Submit out over a required field the user cannot see, with no
          // error rendered anywhere on the sheet. The old `enable()` carried
          // the comment "doesn't matter, but for consistency"; it stopped not
          // mattering when the schema's own "FS-MFRS" default was removed
          // from `ssmt-dei:TypeOfSubmission` (the backend now returns null for
          // both it and its Basis driver), which made an empty Type of
          // Submission the normal cold-load state rather than an impossible
          // one. Disabling also matches what the visible branch below does.
          this.hiddenFields[fieldSchema.qname] = true;
          targetControl.disable({ emitEvent: false });
        } else {
          // Show field, disable it, and set value based on Type of Submission
          this.hiddenFields[fieldSchema.qname] = false;
          targetControl.disable({ emitEvent: false }); // Disable to prevent user modification

          if (typeOfSubmissionValue === 'FS-BNM') {
            targetControl.setValue(
              'Company regulated by Bank Negara Malaysia',
              { emitEvent: false }
            );
          } else {
            targetControl.setValue(
              'Company not regulated by Bank Negara Malaysia',
              { emitEvent: false }
            );
          }
        }
      };

      // Set initial state
      updateFieldBasedOnTypeOfSubmission(typeOfSubmissionControl.value);
      this.reactiveRuleAppliers.push(() =>
        updateFieldBasedOnTypeOfSubmission(typeOfSubmissionControl.value)
      );

      // Watch for changes in Type of Submission
      typeOfSubmissionControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          updateFieldBasedOnTypeOfSubmission(value);
        });
    });
  }

  /**
   * [Not supported in role0] Setup KFI-MFRS/KFI-MPERS exemption field auto-management.
   *
   * When Type of Submission is "KFI-MFRS" or "KFI-MPERS", the exemption field
   * must be set to "Yes". role0 ships FS-MFRS / FS-MPERS only (both conversion
   * phases), so this method is unreachable and has been disabled. Re-enable
   * the caller in ngOnInit (and uncomment this body) only if KFI variants are
   * ever adopted for role0.
   */
  // private setupKFIMFRSExemptionFieldManagement(): void {
  //   const typeOfSubmissionControl = this.filingForm.get(
  //     'ssmt-dei:TypeOfSubmission'
  //   );
  //   const exemptionFieldControl = this.filingForm.get(
  //     'ssmt:DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister'
  //   );
  //
  //   if (!typeOfSubmissionControl || !exemptionFieldControl) {
  //     console.warn('Type of Submission or Exemption field control not found');
  //     return;
  //   }
  //
  //   // Check if exemption field is marked as readonly in schema
  //   const exemptionFieldSchema = this.schema.controls.find(
  //     (c) => c.qname === 'ssmt:DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister'
  //   );
  //   const isReadonly = exemptionFieldSchema?.ui?.readonly || false;
  //
  //   // Helper function to update exemption field based on Type of Submission
  //   const updateExemptionField = (typeOfSubmissionValue: string | null) => {
  //     if (
  //       typeOfSubmissionValue === 'KFI-MFRS' ||
  //       typeOfSubmissionValue === 'KFI-MPERS'
  //     ) {
  //       // Set to "Yes" and disable the field
  //       exemptionFieldControl.setValue('Yes', { emitEvent: false });
  //       exemptionFieldControl.disable({ emitEvent: false });
  //     } else {
  //       // Only enable if field is not marked as readonly in UI config
  //       if (!isReadonly) {
  //         exemptionFieldControl.enable({ emitEvent: false });
  //       }
  //     }
  //   };
  //
  //   // Set initial state
  //   updateExemptionField(typeOfSubmissionControl.value);
  //
  //   // Watch for changes in Type of Submission
  //   typeOfSubmissionControl.valueChanges
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((value) => {
  //       updateExemptionField(value);
  //     });
  // }

  /**
   * Setup automatic Type of Submission based on Accounting Standards
   * MFRS → FS-MFRS, MPERS → FS-MPERS
   */
  private setupTypeOfSubmissionAutoSet(): void {
    const accountingStandardsControl = this.filingForm.get(
      'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'
    );
    const typeOfSubmissionControl = this.filingForm.get('ssmt-dei:TypeOfSubmission');

    if (!accountingStandardsControl || !typeOfSubmissionControl) {
      console.warn('Accounting Standards or Type of Submission control not found');
      return;
    }

    /**
     * @param allowClear `false` for the one-shot call at setup, `true` for every
     *   valueChanges-driven call. An empty Basis at MOUNT is the normal state of
     *   a brand-new (or unmapped-extraction) filing, NOT a user clearing the
     *   dropdown, so the mount pass may not clear: a submission type that came
     *   from the API or from persisted state must survive a Basis that failed to
     *   round-trip, and a value owned by another flow (KFI-*, FS-EPC) must never
     *   be clobbered by a mount. Only a genuine TRANSITION to an empty Basis may
     *   clear.
     *
     *   Historically this guard also protected a `defaultValue: "FS-MFRS"` that
     *   role-0.form.json seeded onto TypeOfSubmission. That default was REMOVED
     *   (2026-07-30) once the backend started returning `null` for both Basis and
     *   TypeOfSubmission — it was presenting an unearned MFRS to the user. A
     *   brand-new filing now legitimately shows an empty Type of submission until
     *   Basis is picked, and the mandatory error surfaces on Basis (the enabled
     *   driver) rather than on this readonly field. Downstream consumers already
     *   tolerate the empty string: `determineTaxonomy` falls back to MFRS,
     *   `conversion-validator.service.ts` gates on a resolved submission type, and
     *   `sheet-validation-context.util.ts` no longer discriminates phase on it
     *   alone. Do NOT reinstate the default.
     */
    const setTypeOfSubmission = (accountingStandards: string | null, allowClear: boolean) => {
      if (accountingStandards === 'Malaysian Financial Reporting Standards') {
        typeOfSubmissionControl.setValue('FS-MFRS', { emitEvent: false });
      } else if (accountingStandards === 'Malaysian Private Entities Reporting Standards') {
        typeOfSubmissionControl.setValue('FS-MPERS', { emitEvent: false });
      } else if (!accountingStandards && allowClear) {
        // Basis was CLEARED (the dropdown's [showClear] X, or the Delete key).
        // Basis and TypeOfSubmission are a derived pair, so the derived half must
        // go with it — TypeOfSubmission is `readonly: true` in role-0.form.json,
        // hence built disabled with no clear icon of its own, so a latched stale
        // value here is uncorrectable by the user while sheet applicability, the
        // MFRS/MPERS schema switch and role 3's gate all keep reading it.
        //
        // Only the two values THIS rule derives are cleared: a submission type
        // that came from another flow (KFI-*, FS-EPC) is never clobbered.
        const current = typeOfSubmissionControl.value;
        if (current === 'FS-MFRS' || current === 'FS-MPERS') {
          typeOfSubmissionControl.setValue('', { emitEvent: false });
        }
      }
      // Any other Basis value (e.g. "Others") leaves TypeOfSubmission untouched.
    };

    // Set initial value — never clears (see allowClear above).
    setTypeOfSubmission(accountingStandardsControl.value, false);
    // allowClear:false — a hydration pass is NOT a user transition. Clearing
    // here would wipe an API/persisted submission type on every extracted
    // filing whose Basis failed to map.
    this.reactiveRuleAppliers.push(() =>
      setTypeOfSubmission(accountingStandardsControl.value, false)
    );

    // Watch for changes in Accounting Standards — a transition MAY clear.
    accountingStandardsControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        setTypeOfSubmission(value, true);
      });
  }

  /**
   * Push the form's current raw value into modifiedStateSubject immediately,
   * without going through the debounced user-edit auto-save path.
   *
   * Mirrors role1's identically-named helper. role0 previously inlined this
   * `updateSheetData` call at its single hydration call site; the driver-clear
   * cascade needs the same push, and two copies of one idiom on one sheet is
   * how the role0/role1 engines drifted in the first place.
   *
   * `skipEditedMark = true`: the cascade is a schema-rule side effect, not a
   * user edit. The user's own clear of the DRIVER already flows through
   * `setupStatePersistence`'s valueChanges pipeline, which owns the pencil icon.
   */
  private syncFormToState(): void {
    const formValue = this.filingForm.getRawValue();
    this.stateService.updateSheetData(
      SHEET_KEYS.filingPrerequisites,
      formValue,
      this.filingForm.valid,
      true
    );
  }

  /**
   * User-initiated driver-clear cascade for the dropdown chain
   *   Origin of company -> Types of company -> Basis of accounting standards
   * (and, through the existing auto-set seam, -> Type of submission).
   *
   * The graph lives in filing-info-driver-clear.rules.ts and is shared with
   * role1 so the two sheets cannot disagree about what resets.
   *
   * THREE THINGS HERE ARE LOAD-BEARING:
   *
   * 1. `emitEvent: true`. Clearing Basis must reach
   *    `setupTypeOfSubmissionAutoSet`'s subscriber, which owns the derived
   *    FS-MFRS/FS-MPERS clear. Emitting also lets the option-filter and
   *    method-field engines re-evaluate against the new emptiness.
   *
   * 2. Registered last as a deliberate default: this subscriber's job is to
   *    OVERWRITE what earlier rules just computed, so running it after them
   *    requires no reasoning about their side effects. Verified 2026-08-10
   *    that position is not load-bearing TODAY — but only because every rule
   *    that writes a value into a cascade target (`lockWhen.equals`,
   *    `disabledWhen.values`) is keyed to a NON-EMPTY literal, so none can
   *    fire on the `''` this cascade writes. Add `""` to a
   *    `disabledWhen.values`, or a `setDefaultWhen` anywhere in the
   *    Origin -> Types of company -> Basis chain, and this must stay last.
   *    Pinned by `filing-info-driver-clear.schema-invariant.spec.ts`.
   *
   * 3. NOT registered in `reactiveRuleAppliers`. Hydration is a different rule:
   *    an extraction-NULL driver renders the dependent empty + DISABLED +
   *    unvalidated (see setupConditionalOptions). This cascade is user-only and
   *    leaves the dependents ENABLED and required, so the user can re-pick.
   *    Hydration patches use `emitEvent: false` and therefore never reach here.
   */
  private setupDriverClearCascade(): void {
    Object.keys(FILING_INFO_DRIVER_CLEAR_EDGES).forEach((driverQname) => {
      const driverControl = this.filingForm.get(driverQname);

      if (!driverControl) {
        console.warn(
          `driverClearCascade: driver control '${driverQname}' not found`
        );
        return;
      }

      driverControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          if (!isClearedValue(value) || this.isDriverClearCascading) {
            return;
          }

          this.isDriverClearCascading = true;
          try {
            resolveDriverClearTargets(driverQname).forEach((targetQname) => {
              const targetControl = this.filingForm.get(targetQname);

              if (!targetControl) {
                return;
              }

              // Always propagate, even when the target is ALREADY empty:
              // an already-empty Basis still needs its `emitEvent: true` to
              // reach setupTypeOfSubmissionAutoSet's subscriber, or a latched
              // FS-MFRS/FS-MPERS in Type of submission survives the cascade.
              // Only the dirty/touched marks are conditional — don't flag a
              // control the user never filled as if they just cleared it.
              const wasAlreadyEmpty = isClearedValue(targetControl.value);
              targetControl.setValue('', { emitEvent: true });
              if (!wasAlreadyEmpty) {
                targetControl.markAsDirty();
                targetControl.markAsTouched();
              }
            });
          } finally {
            this.isDriverClearCascading = false;
          }

          this.syncFormToState();
        });
    });
  }

  /**
   * [Declarative refactor] Setup automatic management of "Method used for
   * representing changes in an entity's equity" (Field #31).
   *
   * Superseded by declarative schema rules in role-0.form.json:
   *   - `readonly: true`
   *   - `defaultValue: "Statement of Changes In Equity"`
   *   - `disabledWhen.field: ssmt-dei:TypeOfSubmission`,
   *     `values: ["FS-MFRS"]`, `setValueTo: ""`, `hideWhenDisabled: true`
   *
   * MPERS → field shown with the default value (readonly).
   * MFRS  → disabledWhen fires → value cleared and field hidden.
   *
   * Kept as commented-out reference. Re-enable the caller in ngOnInit and
   * uncomment this body only if the declarative rule regresses the
   * MPERS-show / MFRS-hide UX.
   */
  // private setupEquityMethodFieldManagement(): void {
  //   const accountingStandardsControl = this.filingForm.get(
  //     'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'
  //   );
  //   const equityMethodControl = this.filingForm.get(
  //     'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity'
  //   );
  //
  //   if (!accountingStandardsControl || !equityMethodControl) {
  //     console.warn('Accounting Standards or Equity Method control not found');
  //     return;
  //   }
  //
  //   // Helper function to manage equity method field
  //   const manageEquityMethodField = (accountingStandards: string | null) => {
  //     if (accountingStandards === 'Malaysian Private Entities Reporting Standards') {
  //       // MPERS: Show field, set value, make readonly
  //       this.hiddenFields['ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity'] = false;
  //       equityMethodControl.setValue('Statement of Changes In Equity', { emitEvent: false });
  //       equityMethodControl.disable({ emitEvent: false });
  //     } else {
  //       // MFRS or other: Hide field, disable, clear value
  //       this.hiddenFields['ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity'] = true;
  //       equityMethodControl.setValue('', { emitEvent: false });
  //       equityMethodControl.disable({ emitEvent: false });
  //     }
  //   };
  //
  //   // Set initial state
  //   manageEquityMethodField(accountingStandardsControl.value);
  //
  //   // Watch for changes in Accounting Standards
  //   accountingStandardsControl.valueChanges
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((value) => {
  //       manageEquityMethodField(value);
  //     });
  // }

  /**
   * Setup method fields management based on 4-field context
   * Fields: Status of Company, Type of Company, Accounting Standards, Type of Submission
   * IMPORTANT: Skips readonly fields (they keep their default values)
   */
  private setupMethodFieldsManagement(): void {
    // Find fields with autoManageByContext flag, excluding readonly fields
    const fieldsToAutoManage = this.schema.controls.filter(
      (control) => control.ui.autoManageByContext && !control.ui.readonly
    );

    if (fieldsToAutoManage.length === 0) {
      return;
    }

    // All 5 method fields (filter out readonly fields and equity method field which is managed separately)
    const allMethodFieldQnames = [
      'ssmt:MethodUsedForPreparingStatementOfFinancialPosition',
      'ssmt:MethodUsedForPreparingStatementOfProfitOrLoss',
      'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome',
      'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity',
      'ssmt:MethodUsedForPreparingStatementOfCashFlows',
    ];

    // Filter out readonly fields and equity method field (managed by setupEquityMethodFieldManagement)
    const allMethodFields = allMethodFieldQnames.filter(qname => {
      // Exclude equity method field - it's managed by its own dedicated method
      if (qname === 'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity') {
        return false;
      }
      const fieldSchema = this.schema.controls.find(control => control.qname === qname);
      return !fieldSchema?.ui?.readonly;
    });

    if (allMethodFields.length === 0) {
      return;
    }

    // Get all 4 trigger controls
    const statusControl = this.filingForm.get('ssmt-dei:StatusOfCompany');
    const typeControl = this.filingForm.get('ssmt-dei:TypeOfCompany');
    const standardsControl = this.filingForm.get(
      'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'
    );
    const submissionControl = this.filingForm.get('ssmt-dei:TypeOfSubmission');

    if (
      !statusControl ||
      !typeControl ||
      !standardsControl ||
      !submissionControl
    ) {
      console.warn(
        'One or more trigger controls not found for method fields management'
      );
      return;
    }

    // Load the conditional mapping from typeDict
    const conditionalMapping =
      this.typeDict.conditionalMappings?.['MethodFieldsEnabledByContext'];
    if (!conditionalMapping || !conditionalMapping.mappings) {
      console.error(
        'Conditional mapping "MethodFieldsEnabledByContext" not found in types.dict.json'
      );
      return;
    }

    const mappings = conditionalMapping.mappings;

    // Helper function to build composite key from 4 field values
    const buildCompositeKey = (): string => {
      const status = statusControl.value || '';
      const type = typeControl.value || '';
      const standards = standardsControl.value || '';
      const submission = submissionControl.value || '';

      // Extract prefix from Type of Submission (e.g., "FS-MFRS" → "FS", "KFI-MPERS" → "KFI")
      const submissionPrefix = submission.split('-')[0] || '';

      // Build composite key: "Public company|Company limited by shares|Malaysian Financial Reporting Standards|KFI"
      return `${status}|${type}|${standards}|${submissionPrefix}`;
    };

    /**
     * True when all four trigger controls carry a value, i.e. the composite key
     * can actually be matched against the mapping table.
     *
     * A cleared driver (the dropdowns' new [showClear] X) contributes an empty
     * segment, so the key misses EVERY mapping and every method field lands in
     * the "hide, disable, clear value" branch below — silently destroying the
     * user's five method selections, which re-selecting the driver restores only
     * as schema defaults. "Key incomplete because a driver is empty" is NOT the
     * same as "key valid but this field is inapplicable"; only the latter clears.
     */
    const hasCompleteContext = (): boolean =>
      !!statusControl.value &&
      !!typeControl.value &&
      !!standardsControl.value &&
      !!submissionControl.value;

    // Helper function to update method fields visibility based on composite key
    const updateMethodFieldsVisibility = (isInitialLoad = false) => {
      // Special handling for FS-EPC: always disable all method fields regardless of accounting standard
      const submissionType = submissionControl.value || '';
      if (submissionType === 'FS-EPC') {
        // Disable all method fields
        allMethodFields.forEach((fieldQname) => {
          const fieldControl = this.filingForm.get(fieldQname);
          if (!fieldControl) return;

          this.hiddenFields[fieldQname] = true;
          fieldControl.disable({ emitEvent: false });
          fieldControl.setValue('', { emitEvent: false });
        });
        // Sync the FS-EPC blackout into persisted state, mirroring role1.
        // The cleared values must reach modifiedStateSubject so the pure
        // validator does not see a stale prior selection that would no
        // longer be valid under FS-EPC's "all method fields blacked out"
        // rule.
        this.syncFormToState();
        return; // Exit early
      }

      // A driver is empty, so the composite key can match no mapping.
      if (!hasCompleteContext()) {
        if (!isInitialLoad) {
          // Mid-session clear: leave values AND visibility exactly as they are
          // until the user re-selects the driver.
          return;
        }
        // INITIAL LOAD with an incomplete context. This is reached in two ways
        // and both must keep the values: a brand-new filing (nothing to lose),
        // and — the case this guard exists for — a REMOUNT after the user
        // cleared a driver, where loadSavedState() has already patched the saved
        // incomplete context plus the user's method selections into the form
        // BEFORE setupMethodFieldsManagement() runs. Falling through to the
        // mapping logic below would blank all five fields, which is the mid-
        // session data loss simply deferred by one navigation.
        //
        // Visibility/enablement still follow the historical "hide until a
        // context is picked" rendering — only the destructive setValue('') is
        // skipped, so nothing the user chose is destroyed.
        allMethodFields.forEach((fieldQname) => {
          const fieldControl = this.filingForm.get(fieldQname);
          if (!fieldControl) return;
          this.hiddenFields[fieldQname] = true;
          fieldControl.disable({ emitEvent: false });
        });
        return;
      }

      // Normal composite key logic
      const compositeKey = buildCompositeKey();
      const mappingValue = mappings[compositeKey];

      // Parse mapping value (can be array or object)
      let enabledFields: string[] = [];
      let disabledWithDefaults: { [key: string]: string } = {};

      if (Array.isArray(mappingValue)) {
        // Simple array structure: all fields are enabled
        enabledFields = mappingValue;
      } else if (mappingValue && typeof mappingValue === 'object') {
        // Object structure: enabled + disabledWithDefaults
        enabledFields = mappingValue.enabled || [];
        disabledWithDefaults = mappingValue.disabledWithDefaults || {};
      } else {
      }

      // Update each method field
      allMethodFields.forEach((fieldQname) => {
        const fieldControl = this.filingForm.get(fieldQname);
        if (!fieldControl) return;

        // Check if field is marked as readonly in schema
        const fieldSchema = this.schema.controls.find((control) => control.qname === fieldQname);
        const isReadonly = fieldSchema?.ui?.readonly || false;

        const isEnabled = enabledFields.includes(fieldQname);
        const hasDisabledDefault =
          disabledWithDefaults.hasOwnProperty(fieldQname);

        if (isEnabled) {
          // Show and enable the field (unless readonly)
          this.hiddenFields[fieldQname] = false;
          if (!isReadonly) {
            fieldControl.enable({ emitEvent: false });
          } else {
          }
        } else if (hasDisabledDefault) {
          // Show but disable the field, set default value
          this.hiddenFields[fieldQname] = false;
          fieldControl.disable({ emitEvent: false });
          fieldControl.setValue(disabledWithDefaults[fieldQname], {
            emitEvent: false,
          });
        } else {
          // Hide and disable the field, clear value
          this.hiddenFields[fieldQname] = true;
          fieldControl.disable({ emitEvent: false });
          fieldControl.setValue('', { emitEvent: false });
        }
      });
      // Sync the cumulative method-field mutations after the forEach so
      // modifiedStateSubject reflects every disabled-default and cleared
      // value the user is shown, mirroring role1. Without this, a late API
      // hydration (which replays this engine via reactiveRuleAppliers rather
      // than through setupStatePersistence's valueChanges pipeline) leaves
      // the form correctly cleared/hidden while modifiedStateSubject — what
      // the pure validators read and what gets submitted — keeps the stale
      // hydrated value.
      this.syncFormToState();
    };

    // Set initial state
    updateMethodFieldsVisibility(true);
    // isInitialLoad:true — applies the "hide until a context is picked"
    // rendering when the composite key is incomplete. `false` returns early and
    // would leave the five method fields visible and enabled under a context
    // that matches no mapping. Both branches preserve VALUES (the destructive
    // setValue('') lives in the complete-context-but-excluded branch, which
    // neither reaches), so this is a rendering choice, not a data-safety one.
    this.reactiveRuleAppliers.push(() => updateMethodFieldsVisibility(true));

    // Watch for changes in ANY of the 4 trigger fields
    [statusControl, typeControl, standardsControl, submissionControl].forEach(
      (control) => {
        control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
          updateMethodFieldsVisibility();
        });
      }
    );
  }

  /**
   * Setup setDefaultWhen rules from schema
   * Dynamically reads all controls with setDefaultWhen configuration and sets up watchers
   * When trigger field equals specified value, sets target field to the configured default value
   */
  private setupSetDefaultWhenRules(): void {
    // Find all controls with setDefaultWhen configuration
    const controlsWithSetDefaultWhen = this.schema.controls.filter(
      (control) => control.ui.setDefaultWhen
    );

    if (controlsWithSetDefaultWhen.length === 0) {
      return;
    }

    controlsWithSetDefaultWhen.forEach((fieldSchema) => {
      const setDefaultWhenConfig = fieldSchema.ui.setDefaultWhen!;
      const { field: triggerFieldQname, equals: triggerValue, setValueTo } = setDefaultWhenConfig;

      const triggerControl = this.filingForm.get(triggerFieldQname);
      const targetControl = this.filingForm.get(fieldSchema.qname);

      if (!triggerControl || !targetControl) {
        console.warn(`setDefaultWhen: Trigger control '${triggerFieldQname}' or target control '${fieldSchema.qname}' not found`);
        return;
      }

      // Helper function to set default value when condition is met
      const applyDefaultWhenRule = (triggerFieldValue: string | null, isInitialLoad: boolean = false) => {
        if (triggerFieldValue === triggerValue) {
          if (isInitialLoad) {
            // On initial load, only set if target field is empty
            if (!targetControl.value || targetControl.value === '') {
              targetControl.setValue(setValueTo, { emitEvent: true });
            }
          } else {
            // On change, always set the default value
            targetControl.setValue(setValueTo, { emitEvent: true });
          }
        }
      };

      // Apply rule on initial load
      applyDefaultWhenRule(triggerControl.value, true);

      // Watch for changes in trigger field
      triggerControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          applyDefaultWhenRule(value, false);
        });
    });
  }

  /**
   * Setup lockWhen rules from schema.
   *
   * When the trigger field equals the configured value, the target field is
   * set to `setValueTo` AND disabled (so the user cannot change it). When the
   * trigger no longer matches, the target is re-enabled.
   *
   * Used for the Basis-of-accounting field: when Origin = "Incorporated
   * outside Malaysia", the only supported value is "Malaysian Financial
   * Reporting Standards".
   *
   * Developer Note (2026-04-23): Product-owner decision — the alternative
   * enum value ("Others") is not, and will not be, supported for that Origin.
   * The template renders this locked state as a disabled text input (rather
   * than a disabled dropdown) so users see it as a fixed value, not a choice.
   */
  private setupLockWhenRules(): void {
    const controlsWithLockWhen = this.schema.controls.filter(
      (control) => control.ui.lockWhen
    );

    if (controlsWithLockWhen.length === 0) {
      return;
    }

    controlsWithLockWhen.forEach((fieldSchema) => {
      const { field: triggerFieldQname, equals: triggerValue, setValueTo } = fieldSchema.ui.lockWhen!;

      const triggerControl = this.filingForm.get(triggerFieldQname);
      const targetControl = this.filingForm.get(fieldSchema.qname);

      if (!triggerControl || !targetControl) {
        console.warn(`lockWhen: Trigger control '${triggerFieldQname}' or target control '${fieldSchema.qname}' not found`);
        return;
      }

      // Item C (2026-08-10): SYSTEM applications (initial mount, hydration
      // re-apply) write SILENTLY. Product ruling: a page load with no user
      // action must retain the sheet's original status — no pencil. The
      // emission is what reached the debounced persist with
      // skipEditedMark:false and lit the pencil. Safe only because item A
      // gave this rule its own syncFormToState(). Mirrors role1.
      const applyLockRule = (triggerFieldValue: string | null, isUserDriven: boolean) => {
        if (triggerFieldValue === triggerValue) {
          targetControl.setValue(setValueTo, { emitEvent: isUserDriven });
          targetControl.disable({ emitEvent: false });
        } else if (
          targetControl.disabled &&
          !isHeldDisabledByAnotherRule(fieldSchema, this.filingForm, 'lockWhen')
        ) {
          // Only re-enable if the field is not otherwise locked — `readonly` in
          // UI config, or a `disabledWhen` rule whose own trigger still matches.
          //
          // 2026-08-14: this used to check `readonly` alone, on the premise that
          // "disabledWhen's own watcher will re-disable on the next emission if
          // its condition still holds". There is no such emission — that watcher
          // fires only when ITS trigger changes — so mounting with Origin inside
          // Malaysia and Types of company "Company limited by guarantee" left
          // Basis editable, because this rule runs after setupFieldDisabling in
          // ngOnInit. The predicate now reads the other rule's trigger directly.
          targetControl.enable({ emitEvent: false });
        }
        // Item A (2026-08-10): role0's own synchronous route into
        // modifiedStateSubject. Until now this rule reached state ONLY via the
        // `emitEvent: true` write landing in the debounced persist, which
        // is why loadSavedState needed ~700ms of setTimeout padding to let that
        // persist beat the baseline snapshot. With this call the emission is no
        // longer load-bearing for state, which is what makes items B and C
        // possible. `skipEditedMark: true` — a schema rule is not a user edit.
        // Mirrors role1's applyLockRule.
        this.syncFormToState();
      };

      // Apply rule on initial load
      applyLockRule(triggerControl.value, false); // SYSTEM: initial mount.
      this.reactiveRuleAppliers.push(() => applyLockRule(triggerControl.value, false)); // SYSTEM: hydration re-apply.

      // Watch for changes in trigger field
      triggerControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          applyLockRule(value, true); // USER-DRIVEN.
        });
    });
  }

  /**
   * Product ruling (2026-08-10): RE-LOCK IMMEDIATELY. `setupDriverClearCascade`
   * clears a `lockWhen` target (e.g. Basis) when an UPSTREAM driver (Types of
   * company) is cleared, and `setupFieldDisabling`'s own `disabledWhen`
   * else-branch then re-enables it because ITS OWN trigger no longer matches
   * — `setupLockWhenRules`'s `applyLockRule` never sees this, because it only
   * watches the Origin trigger, and Origin never changed. Left alone, that
   * leaves an editable, empty Basis dropdown under a foreign Origin the lock
   * exists to forbid entirely (the user could pick MPERS, which neither the
   * editor nor the headless validator would reject — see
   * docs/specifications/sheets/00_filing-prerequisites.md Field #16). So:
   * watch the TARGET's own value, and the instant it goes empty while the
   * trigger is STILL at the locking value, write `setValueTo` straight back
   * and re-disable.
   *
   * This must NOT fire when the trigger itself is what changed (the Origin-
   * clear path): `applyLockRule` in `setupLockWhenRules` already re-enables +
   * leaves the target cleared in that case, and re-reading
   * `triggerControl.value` here (rather than trusting subscription order) is
   * what tells the two paths apart — by the time this fires, Origin has
   * already been updated to whatever the user set it to.
   *
   * DELIBERATELY A SEPARATE METHOD FROM `setupLockWhenRules`, called LATER in
   * `ngOnInit` (after `setupTypeOfSubmissionAutoSet`) — NOT folded into the
   * same forEach pass. `setupTypeOfSubmissionAutoSet` also subscribes to
   * Basis's OWN `valueChanges` (to derive Type of submission), on the SAME
   * control this re-assert watches. RxJS's `EventEmitter`/`Subject` delivers
   * one `next()` call to every subscriber IN SUBSCRIPTION ORDER using the
   * SAME emitted value, even if an earlier subscriber's own `setValue()`
   * triggers a nested re-emission first — the nested emission runs to
   * completion, but the OUTER emission then resumes and still hands its
   * *original, now-stale* value to the remaining subscribers. If this
   * re-assert subscribed to Basis BEFORE `setupTypeOfSubmissionAutoSet` (as
   * it would from inside `setupLockWhenRules`, which runs earlier), the
   * cascade's `''` emission would re-lock Basis first (nested emission sets
   * Type of submission back to `FS-MFRS`), and THEN the outer `''` would
   * still reach `setupTypeOfSubmissionAutoSet`'s subscriber, which would
   * clear Type of submission a second time — landing on `''` instead of
   * `FS-MFRS`. Measured with `role0-lockwhen-cleared-origin.spec.ts` during
   * development; registering this subscription AFTER
   * `setupTypeOfSubmissionAutoSet`'s makes THIS subscriber the last to see
   * the outer emission, so its re-lock (and the nested `FS-MFRS` derivation
   * it triggers) is the final word. Do not merge this back into
   * `setupLockWhenRules` without re-verifying that ordering.
   */
  private setupLockWhenReassert(): void {
    const controlsWithLockWhen = this.schema.controls.filter(
      (control) => control.ui.lockWhen
    );

    controlsWithLockWhen.forEach((fieldSchema) => {
      const { field: triggerFieldQname, equals: triggerValue, setValueTo } = fieldSchema.ui.lockWhen!;

      const triggerControl = this.filingForm.get(triggerFieldQname);
      const targetControl = this.filingForm.get(fieldSchema.qname);

      if (!triggerControl || !targetControl) {
        return;
      }

      targetControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          if (this.isLockWhenReasserting) {
            return;
          }
          if (!isClearedValue(value) || triggerControl.value !== triggerValue) {
            return;
          }
          this.isLockWhenReasserting = true;
          try {
            targetControl.setValue(setValueTo, { emitEvent: true });
            targetControl.disable({ emitEvent: false });
            // Task 7 (2026-08-10): sync the re-locked value into
            // modifiedStateSubject synchronously, mirroring role1's
            // `setupLockWhenReassert` and `setupLockWhenRules`'s own
            // `applyLockRule` above (Item A). Without this, the re-assert
            // relied SOLELY on the `emitEvent: true` write landing in the
            // debounced persist to reach state.
            this.syncFormToState();
          } finally {
            this.isLockWhenReasserting = false;
          }
        });
    });
  }

  /**
   * Template helper: returns true when a field's lockWhen rule is currently
   * active (i.e. the trigger field's current value matches). Drives the
   * disabled-text-input rendering branch in the template.
   */
  isLockActive(control: FormControlSchema): boolean {
    const lock = control.ui.lockWhen;
    if (!lock) return false;
    const trigger = this.filingForm?.get(lock.field);
    if (!trigger || trigger.value !== lock.equals) return false;
    // The trigger matching is necessary but not sufficient: the target
    // control could in principle be re-enabled by another rule while the
    // trigger still matches (e.g. `setupFieldDisabling`'s else-branch, mid-
    // cascade, before the re-assert subscriber below has run). Since
    // 2026-08-10 that window closes synchronously — the same emission that
    // clears the target re-locks it before this getter is ever read from
    // the template — but the disabled check stays as a defensive mirror of
    // the control's REAL state rather than trusting the trigger alone.
    const targetControl = this.filingForm?.get(control.qname);
    return !!targetControl && targetControl.disabled;
  }

  /**
   * Template helper: returns the locked value for a field whose lockWhen
   * rule is currently active. Returns empty string if not locked.
   */
  getLockedValue(control: FormControlSchema): string {
    return control.ui.lockWhen?.setValueTo ?? '';
  }

  /**
   * Auto-fill demo data
   */
  // autoFillDemo(): void {
  //   const currentYear = new Date().getFullYear();
  //   const previousYear = currentYear - 1;
  //   const twYearsAgo = currentYear - 2;

  //   const demoData = {
  //     'ssmt-dei:NewCompanyRegistrationNumber': '202001012345',
  //     'ssmt-dei:CompanyRegistrationNumber': '202001012345',
  //     'ssmt-dei:NameOfReportingEntity': 'ABC Manufacturing Sdn Bhd',
  //     'ssmt-dei:FormerNameOfCompany': '',
  //     'ssmt-dei:OriginOfCompany': 'Incorporated in Malaysia',
  //     'ssmt-dei:StatusOfCompany': 'Private company',
  //     'ssmt-dei:TypeOfCompany': 'Company limited by shares',
  //     'ssmt-dei:ApplicationOfSubmission': 'Ordinary filing',
  //     'ssmt-dei:StatutoryDeclarationForRectification': '',
  //     'ssmt-dei:CourtOrderReferenceNumber': '',
  //     'ssmt-dei:DisclosureOfFinancialStatementsPreparationForCurrentSubmission':
  //       'Subsequent preparation of financial statements',
  //     'ssmt-dei:CompanyCurrentFinancialYearStartDate': new Date(
  //       previousYear,
  //       0,
  //       1
  //     ),
  //     'ssmt-dei:CompanyCurrentFinancialYearEndDate': new Date(
  //       previousYear,
  //       11,
  //       31
  //     ),
  //     'ssmt-dei:CompanyPreviousFinancialYearStartDate': new Date(
  //       twYearsAgo,
  //       0,
  //       1
  //     ),
  //     'ssmt-dei:CompanyPreviousFinancialYearEndDate': new Date(
  //       twYearsAgo,
  //       11,
  //       31
  //     ),
  //     'ssmt-dei:StatusOfCarryingOnBusinessDuringFinancialYear': 'Active',
  //     'ssmt-dei:DisclosureOfFinancialStatementsAuditStatus': 'Audited',
  //     'ssmt-dei:AuditExemptionCategory': '',
  //     'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements':
  //       'MPERS',
  //     'ssmt-dei:DisclosureOfOtherAccountingStandardsApplied': '',
  //     'ssmt-dei:TypeOfSubmission': 'FS-MPERS',
  //     'ssmt-dei-ee-mfrs:NatureOfFinancialStatements': 'Separate',
  //     'ssmt-dei:DescriptionOfPresentationCurrency': 'MYR',
  //     'ssmt-dei:DescriptionOfFunctionalCurrency': 'MYR',
  //     'ssmt-dei:LevelOfRoundingUsedInFinancialStatements': 'Actuals',
  //     'ssmt-dei:NameAndVersionOfSoftwareUsedToGenerateXBRLFile':
  //       'MBRS_Preparation_Tool v2.2',
  //     'ssmt-dei:TaxonomyVersion': 'SSMxT_2022v1.0',
  //     'ssmt:DisclosureOnWhetherCompanysSharesAreTradedOnAnyOfficialStockExchange':
  //       'No',
  //     // Bank Negara field is auto-managed by setupBankNegaraFieldManagement() based on Type of Submission
  //     // Method fields are shown/hidden by setupFieldDisabling() based on Type of Submission (FS-* vs KFI-*)
  //     'ssmt:MethodUsedForPreparingStatementOfFinancialPosition':
  //       'Current-Noncurrent',
  //     'ssmt:MethodUsedForPreparingStatementOfProfitOrLoss': 'Nature of expense',
  //     'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome': 'Before tax',
  //     'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity':
  //       'Statement of changes in equity',
  //     'ssmt:MethodUsedForPreparingStatementOfCashFlows': 'Indirect',
  //     'ssmt:DisclosureOnWhetherComparativePeriodValuesAreRestated': 'No',
  //     'ssmt:DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister':
  //       'No',
  //   };

  //   this.filingForm.patchValue(demoData);
  //   this.filingForm.markAsDirty();

  //   this.messageService.add({
  //     severity: 'success',
  //     summary: 'Demo Data Loaded',
  //     detail: 'Form has been filled with sample data',
  //     life: 3000,
  //   });

  //   // Auto-determine standard
  //   this.determineStandardFromFormData();
  // }

  /**
   * Auto-determine MFRS/MPERS based on form data
   */
  private determineStandardFromFormData(): void {
    const statusOfCompany = this.filingForm.get(
      'ssmt-dei:StatusOfCompany'
    )?.value;
    const sharesTraded = this.filingForm.get(
      'ssmt:DisclosureOnWhetherCompanysSharesAreTradedOnAnyOfficialStockExchange'
    )?.value;
    const bankNegaraRegulated = this.filingForm.get(
      'ssmt:DisclosureOfWhetherCompanyRegulatedByBankNegaraMalaysiaAtFinancialYearEnd'
    )?.value;

    let determinedStandard = 'MPERS'; // Default
    let typeOfSubmission = 'FS-MPERS';

    // MFRS determination rules
    if (
      statusOfCompany === 'Public company' ||
      sharesTraded === 'Listed' ||
      bankNegaraRegulated === 'Company regulated by Bank Negara Malaysia'
    ) {
      determinedStandard = 'MFRS';
      typeOfSubmission = 'FS-MFRS';
    }

    // Update form
    this.filingForm.patchValue({
      'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements':
        determinedStandard,
      'ssmt-dei:TypeOfSubmission': typeOfSubmission,
    }, { emitEvent: false });
  }

  /**
   * Cancel changes and redirect to XBRL files page
   * If no changes, redirect directly
   * If there are changes, show confirmation dialog first
   */
  onCancel(): void {
    // Nothing to cancel — do NOT raise "Any unsaved changes will be lost" over
    // a form whose values already match the original. The term is the
    // modified-vs-original verdict, not `filingForm.pristine`: pristine never
    // recovers after the user hand-reverts a field, so it kept the prompt
    // firing with nothing to discard. See `hasUnsubmittedChanges`.
    if (!this.hasUnsubmittedChanges()) {
      return;
    }

    // If there are changes, show confirmation dialog
    this.confirmationService.confirm({
      header: 'Cancel Changes',
      message: 'Any unsaved changes will be lost. Do you want to continue?',
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      closable: false,
      acceptButtonStyleClass: '!min-w-[80px] xlplus:!min-w-[100px] !text-sm xlplus:!text-base !bg-taxpod-blue !border-taxpod-blue !font-lato !font-bold',
      rejectButtonStyleClass: '!min-w-[80px] xlplus:!min-w-[100px] !border-taxpod-blue !text-taxpod-blue !border-[2px] !text-sm xlplus:!text-base hover:!bg-[#E0F2FE] !bg-white',
      accept: () => {
        // Revert all edited data back to initial state
        this.stateService.cancelSheetChanges(SHEET_KEYS.filingPrerequisites);
        this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
        // Re-finalize: loadSavedState() re-opened the initializingSheets
        // bracket via markSheetInitializing(); only finalizeInit() closes
        // it. Fire-and-forget — the redirect below does not depend on it.
        void this.finalizeInit();
        // Mark form as pristine to disable Submit button
        this.filingForm.markAsPristine();
        // Guarantee the per-conversion validation cache is gone after Cancel.
        // Phase 1 (Filing Prerequisites) has its OWN cancel handler — it does
        // not route through SubmitForValidationComponent — so it needs the same
        // suppression: invalidate now AND drop the trailing post-cancel
        // re-baseline write (finalizeInit() above persists it asynchronously,
        // after the navigation-away wipe) so it cannot re-create
        // mbrs-validation-cache-<id>. Auto-clears on the next form entry
        // (setConversionId).
        this.stateService.abandonValidationCache(
          this.stateService.getConversionId(),
        );
        // Redirect to XBRL files list page
        this.router.navigate(['/mbrs/xbrl-files']);
      },
      reject: () => {
        // Do nothing, stay on page
      }
    });
  }

  /**
   * Revert to saved state (original state from localStorage)
   */
  onRevert(): void {
    this.stateService.cancelSheetChanges(SHEET_KEYS.filingPrerequisites);
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    // Re-finalize: loadSavedState() re-opened the initializingSheets
    // bracket via markSheetInitializing(); only finalizeInit() closes it.
    void this.finalizeInit();
    this.messageService.add({
      severity: 'info',
      summary: 'Changes Reverted',
      detail: 'Form reverted to last saved state',
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Check if form has unsaved modifications
   */
  isModified(): boolean {
    return this.stateService.isSheetModified(SHEET_KEYS.filingPrerequisites);
  }

  /**
   * Clear form data
   */
  clearForm(): void {
    this.filingForm.reset();
    this.stateService.clearSheetData(SHEET_KEYS.filingPrerequisites);

    // Restore readonly default values
    this.filingForm.patchValue({
      'ssmt-dei:NameAndVersionOfSoftwareUsedToGenerateXBRLFile':
        'MBRS_Preparation_Tool v2.2',
      'ssmt-dei:TaxonomyVersion': 'SSMxT_2022v1.0',
    }, { emitEvent: false });

    this.messageService.add({
      severity: 'info',
      summary: 'Form Cleared',
      detail: 'All form data has been cleared',
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Get dropdown options for a field
   * Returns filtered options if available, otherwise returns all options
   */
  getDropdownOptions(enumSource?: string, qname?: string): any[] {
    if (!enumSource) return [];

    // Check if there are filtered options for this field
    if (qname && this.filteredDropdownOptions[qname]) {
      return this.filteredDropdownOptions[qname];
    }

    // Return all options
    const enumKey = enumSource.replace('type:', '');
    return this.dropdownOptions[enumKey] || [];
  }

  /**
   * Get static dropdown options
   */
  getStaticOptions(options?: string[]): any[] {
    if (!options) return [];
    return options.map((opt) => ({ label: opt, value: opt }));
  }

  /**
   * Apply input filter based on schema configuration
   * Now uses shared utility function from utils/input-filter.ts
   */
  applyInputFilter = (event: Event, filterType: string | undefined, maxLength: number | undefined, qname: string, trimLeadingZeros?: boolean): void => {
    applyInputFilter(event, filterType, maxLength, qname, this.filingForm, trimLeadingZeros);
  };

  /**
   * Handle paste event with input filtering
   * Now uses shared utility function from utils/input-filter.ts
   */
  onPasteFilter = (event: ClipboardEvent, filterType: string | undefined, maxLength: number | undefined, qname: string, trimLeadingZeros?: boolean): void => {
    applyPasteFilter(event, filterType, maxLength, qname, this.filingForm, trimLeadingZeros);
  };

  /**
   * Check if field should be shown
   */
  shouldShowField(control: FormControlSchema): boolean {
    // Check if field is explicitly hidden in UI config
    if (control.ui.hidden === true) {
      return false;
    }

    // Check hideAfterFirstConversion flag
    // Fields with this flag should only be shown on first conversion
    // (when filingInfo & filingPrerequisites have data, but all other roles are empty)
    if (control.ui.hideAfterFirstConversion === true) {
      // If NOT first conversion (other roles have data), hide the field
      if (!this.isFirstConversion) {
        return false;
      }
      // If first conversion, continue to other checks (show the field)
    }

    // Check if field is hidden due to hideWhenDisabled
    if (this.hiddenFields[control.qname]) {
      return false;
    }

    // Check requiredIf condition
    if (control.requiredIf) {
      const dependentControl = this.filingForm.get(control.requiredIf.qname);
      return dependentControl?.value === control.requiredIf.equals;
    }

    // Check requiredIfAny condition
    if (control.requiredIfAny) {
      return control.requiredIfAny.some((condition) => {
        const dependentControl = this.filingForm.get(condition.qname);
        return dependentControl?.value === condition.equals;
      });
    }

    return true;
  }

  /**
   * Check if field is required
   */
  /**
   * Does this field's label get a `*`?
   *
   * Single-sourced through `isControlMandatory` so every sheet answers this the
   * same way. Each sheet used to hand-roll it and understood only the
   * declaration forms its own schema happened to use, which is how a mechanism
   * moved between sheets could silently stop marking. See `isControlMandatory` in `required-field.util.ts`.
   *
   * NOTE the readonly/`disabledWhen` fields on this sheet DO get a `*` even
   * though they can never raise a required error — the marker tracks the
   * schema's declaration, not whether an error can currently fire (PO rule,
   * 2026-08-11). Do not "align" this with `deriveDisabledKeys` in the pure
   * validator, which deliberately answers the different question.
   */
  isFieldRequired(control: FormControlSchema): boolean {
    return isControlMandatory(control as any, { selfData: this.filingForm.getRawValue() });
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.filingForm.get(qname);
  }

  /**
   * Get datepicker input class based on validation state
   * Returns 'datepicker-input-invalid' if control is invalid and touched
   */
  getDatepickerInputClass(qname: string): string {
    const control = this.filingForm.get(qname);
    if (control && control.invalid && control.touched && !control.disabled) {
      return 'datepicker-input-invalid';
    }
    return '';
  }

  /**
   * Get validation warnings for a specific field
   * Returns array of warning results (BLACK text with asterisk)
   */
  getFieldWarnings(qname: string): WarningResult[] {
    return this.fieldWarnings[qname] || [];
  }

  /**
   * Check if field has any validation warnings
   * Used to show asterisk (*) next to field label in edited mode
   */
  hasWarnings(qname: string): boolean {
    return this.fieldWarnings[qname]?.length > 0;
  }

  /**
   * Get error message for a field
   * Returns custom error message from schema if defined, otherwise returns default message
   * @param control - The form control schema
   * @param errorType - The type of error ('required' | 'pattern')
   * @returns The error message string
   */
  getErrorMessage(control: FormControlSchema, errorType: 'required' | 'pattern'): string {
    // Check if custom error message is defined in schema
    if (control.error && control.error[errorType]) {
      return control.error[errorType]!;
    }

    // Return default error messages
    if (errorType === 'required') {
      return control.error?.required ?? `${control.label} is required`;
    }

    if (errorType === 'pattern') {
      return control.error?.pattern ?? 'Invalid format';
    }

    return 'Validation error';
  }

  /**
   * Split warning message into parts (only asterisk * is red, parentheses stay black)
   * Returns array of objects with text and isAsterisk flag for rendering
   * Example: "(*) Some text" → ["(", "*", ")", " Some text"]
   */
  splitWarningMessage(message: string): { text: string; isAsterisk: boolean }[] {
    const parts: { text: string; isAsterisk: boolean }[] = [];
    const regex = /\(\*\)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(message)) !== null) {
      // Add text before (*)
      if (match.index > lastIndex) {
        parts.push({
          text: message.substring(lastIndex, match.index),
          isAsterisk: false
        });
      }

      // Add ( - black
      parts.push({
        text: '(',
        isAsterisk: false
      });

      // Add * - red
      parts.push({
        text: '*',
        isAsterisk: true
      });

      // Add ) - black
      parts.push({
        text: ')',
        isAsterisk: false
      });

      lastIndex = regex.lastIndex;
    }

    // Add remaining text after last (*)
    if (lastIndex < message.length) {
      parts.push({
        text: message.substring(lastIndex),
        isAsterisk: false
      });
    }

    return parts;
  }

  /**
   * Type 1: Check if field should be disabled (visible but read-only)
   * Returns true if field is disabled based on schema's disabledWhen configuration
   */
  shouldDisableField(fieldQname: string): boolean {
    const control = this.filingForm.get(fieldQname);
    return control ? control.disabled : false;
  }

  /**
   * Type 2: Check if field should be hidden (not rendered)
   * Returns true if field should be completely hidden
   */
  shouldHideField(fieldQname: string): boolean {
    return this.hiddenFields[fieldQname] === true;
  }

  /**
   * Type 3: Check if field should be shown in specific section
   * Role-1 has single section, so always return true
   */
  shouldShowInSection(fieldQname: string, sectionId: string): boolean {
    return true; // Role-1 has no section-based hiding
  }

  onInputChange(event: InputEvent): void {
    handleDateInputChange(event, this.filingForm);
  }

  /** A mouse paste never reaches PrimeNG's model on its own — see `handleDatePaste`. */
  onDatePaste(event: ClipboardEvent): void {
    handleDatePaste(event, this.filingForm);
  }

}
